import { useEffect, useState, useCallback, useMemo, useRef, type ChangeEvent, type SyntheticEvent, type MutableRefObject } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  InputAdornment,
  Divider,
  CircularProgress,
  Slider,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import LinkPagination from '../components/LinkPagination';
import DetailHeader from '../components/DetailHeader';
import CallStatusChip from '../components/CallStatusChip';
import ContactStatusChip from '../components/ContactStatusChip';
import CampaignStatusChip from '../components/CampaignStatusChip';
import {
  ArrowBack,
  PlayArrow,
  Pause,
  Cancel,
  Refresh,
  Code,
  People,
  HelpOutline,
  Search,
  Phone,
  PhoneInTalk,
  Verified,
  OpenInNew,
  Settings,
  Schedule,
  Dialpad,
  Hub,
  Repeat,
  Campaign as CampaignIcon,
  Insights,
  Timeline,
  FileCopy,
  Download,
  AccessTime,
  Add,
  Delete,
  RestartAlt,
  Email,
  Description,
} from '@mui/icons-material';
import PsychologyIcon from '@mui/icons-material/Psychology';
import Tooltip from '@mui/material/Tooltip';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { RequirePermission, usePermissions } from '../contexts/PermissionContext';
import api from '../services/api';
import { uploadMediaFileDirect } from '../services/mediaUpload';
import CampaignReportModal from '../components/CampaignReportModal';
import ContactsViewerModal from '../components/ContactsViewerModal';
import ImportProgressIndicator, { type ImportProgressData } from '../components/ImportProgressIndicator';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { useCampaignWebSocket } from '../hooks/useCampaignWebSocket';
import { useCampaignRealtimeListRefresh } from '../hooks/useCampaignRealtimeListRefresh';
import { useListSearchParams } from '../hooks/useListSearchParams';
import LinkTableCell from '../components/LinkTableCell';
import CallConsumptionDisplay from '../components/CallConsumptionDisplay';
import CampaignConsumptionDisplay from '../components/CampaignConsumptionDisplay';
import ContactConsumptionDisplay from '../components/ContactConsumptionDisplay';
import CampaignConcurrencyConflictDialog from '../components/CampaignConcurrencyConflictDialog';
import CampaignConversionEmailControl from '../components/CampaignConversionEmailControl';
import ReverseIvrDtmfEditor from '../components/ReverseIvrDtmfEditor';
import WaveformPlayer from '../components/WaveformPlayer';
import WaveformPlayerField from '../components/WaveformPlayerField';
import type {
  Campaign,
  Script,
  CampaignStats,
  CampaignContactListItem,
  Agent,
  DayOperatingHours,
  CallConsumptionSegment,
  CallSegmentTotals,
  CallSegmentType,
  CallStatus,
  CampaignTransferTargetType,
} from '../types';
import { formatPhone } from '../utils/phone';
import { formatDateTime } from '../utils/date';
import { useNumberFormat } from '../hooks/useNumberFormat';
import ButtonTabs from '../components/ButtonTabs';
import {
  getReverseIvrAudioDisplayName,
  getReverseIvrAudioDownloadFilename,
  normalizeReverseIvrAudioOption,
} from '../utils/reverseIvrAudioOptions';
import {
  CAMPAIGN_TRANSFER_TARGET_FALLBACK_KEY,
  CAMPAIGN_DETAIL_SUSPENDED_REASON_MESSAGE_KEYS,
  CAMPAIGN_DETAIL_SUSPENDED_REASON_TITLE_KEYS,
  CAMPAIGN_SUSPENDED_REASON_TOOLTIP_KEYS,
  buildCampaignSuspendedReasonI18nOptions,
  getCampaignDisplayStatusState,
} from '../utils/campaignOperationalStatus';
import {
  getCampaignConcurrencyConflict,
  type CampaignConcurrencyConflictPayload,
} from '../utils/campaignConcurrencyConflict';
import { parseNotificationEmails } from '../utils/notificationEmails';
import usePageTitle from '../hooks/usePageTitle';
import {
  buildScheduleEntriesFromOperatingHours,
  DEFAULT_TENANT_OPERATING_HOURS,
  normalizeTenantOperatingHours,
} from '../utils/tenantOperatingHours';
import {
  buildIvrDtmfMapPayload,
  createIvrRule,
  getIvrDtmfValidationErrorKey,
  parseIvrDtmfRules,
  type IvrDtmfRule,
} from '../utils/reverseIvrDtmf';

interface CampaignTransferUserOption {
  id: string;
  name: string;
  extension: string;
}

interface CampaignTransferGroupOption {
  id: string;
  name: string;
  strategy: string;
  membersCount: number;
}

interface ReverseIvrAudioOption {
  id: string;
  name: string;
  displayName?: string | null;
  originalFilename?: string | null;
  durationSeconds?: number | null;
  fileSize?: number;
}

interface CampaignPreCallStatus {
  enabled: boolean;
  scriptId?: string;
  policy?: {
    mode: 'enrichment_only' | 'gate';
    failurePolicy: 'retry' | 'skip' | 'proceed';
  };
  stateCounts: Record<string, number>;
  metrics?: {
    executions: number;
    proceeds: number;
    retries: number;
    skips: number;
    failed: number;
    averageExecutionMs?: number | null;
  };
  breaker: {
    state: 'closed' | 'open' | 'half_open';
    retryAfterSeconds?: number;
  };
}

type CloneContactSource = 'campaign_contacts' | 'uploaded_file';
type CloneIvrAudioSource = 'keep' | 'existing' | 'uploaded';

interface CloneFiltersState {
  excludeConverted: boolean;
  excludeOptout: boolean;
  excludeReached: boolean;
  excludeNoValidPhones: boolean;
  excludeHasDtmf: boolean;
  excludeHeardAudioToEnd: boolean;
  excludeInvalidPhones: boolean;
  excludeMismatchPhones: boolean;
}

interface CloneHistoricalImportFiltersState {
  removePreviouslyConvertedContacts: boolean;
  removePreviouslyReachedContacts: boolean;
  removePreviouslyOptoutContacts: boolean;
}

/**
 * Computes the next inclusive status selection for summary filter cards.
 *
 * An empty selection is treated as "all visible statuses included" so the
 * detail page can keep URL filters concise while still presenting a clear
 * checked/unchecked state in the UI.
 */
function getNextInclusiveFilters(
  currentFilters: string[],
  toggledFilter: string | string[],
  allVisibleFilters: string[],
): string[] {
  const uniqueVisibleFilters = Array.from(new Set(allVisibleFilters));
  const toggledFilters = Array.from(
    new Set(Array.isArray(toggledFilter) ? toggledFilter : [toggledFilter]),
  );
  if (toggledFilters.some((value) => !uniqueVisibleFilters.includes(value))) {
    return currentFilters;
  }

  if (currentFilters.length === 0) {
    const next = uniqueVisibleFilters.filter((value) => !toggledFilters.includes(value));
    return next.length === uniqueVisibleFilters.length ? [] : next;
  }

  const isToggledGroupFullySelected = toggledFilters.every((value) => currentFilters.includes(value));
  if (isToggledGroupFullySelected) {
    const next = currentFilters.filter((value) => !toggledFilters.includes(value));
    return next.length === 0 || next.length === uniqueVisibleFilters.length ? [] : next;
  }

  const next = Array.from(new Set([...currentFilters, ...toggledFilters]));
  return next.length === uniqueVisibleFilters.length ? [] : next;
}

function getIsolatedInclusiveFilters(
  isolatedFilter: string | string[],
  allVisibleFilters: string[],
): string[] {
  const uniqueVisibleFilters = Array.from(new Set(allVisibleFilters));
  const isolatedFilters = Array.from(
    new Set(Array.isArray(isolatedFilter) ? isolatedFilter : [isolatedFilter]),
  ).filter((value) => uniqueVisibleFilters.includes(value));

  if (isolatedFilters.length === 0 || isolatedFilters.length === uniqueVisibleFilters.length) {
    return [];
  }

  return isolatedFilters;
}

const CONTACT_RESULTS_PARAM_KEYS = {
  page: 'contacts_page',
  pageSize: 'contacts_page_size',
  ordering: 'contacts_ordering',
  status: 'contacts_status',
  search: 'contacts_search',
  preCallState: 'contacts_pre_call_state',
} as const;

const CALL_RESULTS_PARAM_KEYS = {
  page: 'calls_page',
  pageSize: 'calls_page_size',
  ordering: 'calls_ordering',
  status: 'calls_status',
  search: 'calls_search',
} as const;

const DETAIL_RESULTS_QUERY_KEYS = [
  ...Object.values(CONTACT_RESULTS_PARAM_KEYS),
  ...Object.values(CALL_RESULTS_PARAM_KEYS),
] as const;

const IMPORT_PROGRESS_PHASE_ORDER = ['downloading', 'parsing', 'validating', 'processing', 'saving', 'finalizing'] as const;
const STATUS_CARD_DOUBLE_CLICK_DELAY_MS = 220;

function getImportProgressPhaseIndex(phase?: string): number {
  if (!phase) {
    return -1;
  }
  return IMPORT_PROGRESS_PHASE_ORDER.indexOf(phase as typeof IMPORT_PROGRESS_PHASE_ORDER[number]);
}

function pickMostAdvancedImportProgress(
  websocketProgress: ImportProgressData | null,
  persistedProgress: ImportProgressData | null,
): ImportProgressData | null {
  if (!websocketProgress) {
    return persistedProgress;
  }
  if (!persistedProgress) {
    return websocketProgress;
  }

  const websocketPhaseIndex = getImportProgressPhaseIndex(websocketProgress.phase);
  const persistedPhaseIndex = getImportProgressPhaseIndex(persistedProgress.phase);

  if (persistedPhaseIndex !== websocketPhaseIndex) {
    return persistedPhaseIndex > websocketPhaseIndex ? persistedProgress : websocketProgress;
  }

  if ((persistedProgress.processedRows ?? 0) !== (websocketProgress.processedRows ?? 0)) {
    return (persistedProgress.processedRows ?? 0) > (websocketProgress.processedRows ?? 0)
      ? persistedProgress
      : websocketProgress;
  }

  if ((persistedProgress.progress ?? 0) !== (websocketProgress.progress ?? 0)) {
    return (persistedProgress.progress ?? 0) > (websocketProgress.progress ?? 0)
      ? persistedProgress
      : websocketProgress;
  }

  return persistedProgress;
}

const CAMPAIGN_TYPE_LABEL_KEYS: Record<'ai' | 'reverse_ivr', string> = {
  ai: 'pages:campaigns.create.basicInfo.campaignTypeAi',
  reverse_ivr: 'pages:campaigns.create.basicInfo.campaignTypeReverseIvr',
};

const DEFAULT_IVR_MAX_AUDIO_SECONDS = 27;
const DEFAULT_CLONE_FILTERS: CloneFiltersState = {
  excludeConverted: true,
  excludeOptout: true,
  excludeReached: true,
  excludeNoValidPhones: true,
  excludeHasDtmf: true,
  excludeHeardAudioToEnd: true,
  excludeInvalidPhones: true,
  excludeMismatchPhones: true,
};
const DEFAULT_CLONE_HISTORICAL_IMPORT_FILTERS: CloneHistoricalImportFiltersState = {
  removePreviouslyConvertedContacts: false,
  removePreviouslyReachedContacts: false,
  removePreviouslyOptoutContacts: false,
};

function extractMediaPreviewUrl(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const data = payload as Record<string, unknown>;
  const candidate = data.downloadUrl ?? data.download_url ?? data.url;
  if (typeof candidate !== 'string') {
    return null;
  }
  const normalized = candidate.trim();
  return normalized.length > 0 ? normalized : null;
}

export default function CampaignDetailPage() {
  const { t, i18n } = useTranslation(['pages', 'common']);
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const handleCampaignBack = useCallback(() => {
    navigate('/campaigns');
  }, [navigate]);


  // Cost visibility permissions
  const { hasPermission } = usePermissions();
  const canViewCostCredits = hasPermission('view_metrics');
  const canViewCostCurrency = hasPermission('view_metrics');

  // Number formatting
  const { formatNumber, formatPercent, formatCurrency, formatDuration, formatDecimal, formatLatency, locale } = useNumberFormat();

  const eventLabels = useMemo(
    () => (t('pages:scripts.eventTypes', { returnObjects: true }) || {}) as Record<string, string>,
    [t]
  );

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const campaignDescriptor = campaign?.name || campaign?.id;
  usePageTitle(t('pages:campaigns.title'), campaignDescriptor);
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const billedSeconds = stats?.totalBilledSeconds ?? campaign?.totalBilledSeconds ?? 0;
  const effectiveSeconds = stats?.totalDuration ?? campaign?.totalDuration ?? 0;
  const hasBilledMinutes = billedSeconds > 0;
  const campaignAiBilledSeconds = stats?.aiBilledSeconds ?? 0;
  const campaignAiGrossBilledSeconds = stats?.aiGrossBilledSeconds ?? campaignAiBilledSeconds;
  const campaignAiRefundedSeconds = stats?.aiRefundedSeconds ?? 0;
  const campaignHumanBilledSeconds = stats?.telephonyBilledSeconds ?? 0;
  const campaignHumanGrossBilledSeconds = stats?.telephonyGrossBilledSeconds ?? campaignHumanBilledSeconds;
  const campaignHumanRefundedSeconds = stats?.telephonyRefundedSeconds ?? 0;
  const campaignAiDurationSeconds = stats?.aiDurationSeconds ?? 0;
  const campaignHumanDurationSeconds = stats?.telephonyDurationSeconds ?? 0;
  const [contacts, setContacts] = useState<CampaignContactListItem[]>([]);
  const [scripts, setScripts] = useState<Script[]>([]);
  const [preCallStatus, setPreCallStatus] = useState<CampaignPreCallStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [concurrencyConflict, setConcurrencyConflict] = useState<CampaignConcurrencyConflictPayload | null>(null);

  // Clone dialog
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneFilters, setCloneFilters] = useState<CloneFiltersState>(DEFAULT_CLONE_FILTERS);
  const [cloneHistoricalImportFilters, setCloneHistoricalImportFilters] = useState<CloneHistoricalImportFiltersState>(
    DEFAULT_CLONE_HISTORICAL_IMPORT_FILTERS,
  );
  const [cloneName, setCloneName] = useState('');
  const [cloneContactSource, setCloneContactSource] = useState<CloneContactSource>('campaign_contacts');
  const [cloneContactsFile, setCloneContactsFile] = useState<File | null>(null);
  const [cloneResourcesLoading, setCloneResourcesLoading] = useState(false);
  const [cloneResourcesError, setCloneResourcesError] = useState<string | null>(null);
  const [cloneSubmitting, setCloneSubmitting] = useState(false);
  const [cloneSubmitPhase, setCloneSubmitPhase] = useState<'uploading_contacts' | 'uploading_audio' | 'cloning' | null>(null);
  const [cloneUploadProgress, setCloneUploadProgress] = useState<number | null>(null);
  const [cloneReverseIvrAudioOptions, setCloneReverseIvrAudioOptions] = useState<ReverseIvrAudioOption[]>([]);
  const [cloneIvrAudioSource, setCloneIvrAudioSource] = useState<CloneIvrAudioSource>('keep');
  const [cloneSelectedReverseIvrAudioId, setCloneSelectedReverseIvrAudioId] = useState<string>('');
  const [cloneIvrAudioFile, setCloneIvrAudioFile] = useState<File | null>(null);
  const [cloneIvrAudioDurationSeconds, setCloneIvrAudioDurationSeconds] = useState<number | null>(null);
  const [cloneIvrAudioPreviewUrl, setCloneIvrAudioPreviewUrl] = useState<string | null>(null);
  const [cloneIvrAudioError, setCloneIvrAudioError] = useState<string | null>(null);
  const [cloneIvrMaxAudioSeconds, setCloneIvrMaxAudioSeconds] = useState<number>(DEFAULT_IVR_MAX_AUDIO_SECONDS);

  // Configuration modal
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [configEditing, setConfigEditing] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [resourcesError, setResourcesError] = useState<string | null>(null);
  const [providerResourcesByProvider, setProviderResourcesByProvider] = useState<
    Record<string, { agents: Agent[] }>
  >({});
  const [availableAgents, setAvailableAgents] = useState<Agent[]>([]);
  const [availableScripts, setAvailableScripts] = useState<Script[]>([]);
  const [configSelectedScriptIds, setConfigSelectedScriptIds] = useState<string[]>([]);
  const [transferTelephonyPackEnabled, setTransferTelephonyPackEnabled] = useState(false);
  const [transferUsers, setTransferUsers] = useState<CampaignTransferUserOption[]>([]);
  const [transferGroups, setTransferGroups] = useState<CampaignTransferGroupOption[]>([]);
  const [configTransferTargetType, setConfigTransferTargetType] = useState<CampaignTransferTargetType | ''>('');
  const [configTransferTargetId, setConfigTransferTargetId] = useState<string>('');
  const [configSuspendWhenNoTransferOperatorAvailable, setConfigSuspendWhenNoTransferOperatorAvailable] = useState(true);
  const [configSelectedAgentId, setConfigSelectedAgentId] = useState('');
  const [maxAllowedConcurrent, setMaxAllowedConcurrent] = useState<number | null>(null);
  const [operatingHours, setOperatingHours] = useState<Record<string, DayOperatingHours>>(DEFAULT_TENANT_OPERATING_HOURS);

  interface ScheduleEntry {
    days: string[];
    allDay?: boolean;
    start: string | null;
    end: string | null;
  }
  interface ScheduleError {
    entryIndex: number;
    type: 'no_days' | 'time_invalid' | 'outside_limits' | 'overlap';
    message: string;
  }
  const [scheduleErrors, setScheduleErrors] = useState<ScheduleError[]>([]);
  const [configForm, setConfigForm] = useState({
    name: '',
    description: '',
    notificationEmails: [] as string[],
    automaticConversionEmailsEnabled: true,
    maxConcurrentCalls: 1,
    maxRetries: 3,
    retryDelayMinSeconds: 0,
    retryDelayMaxSeconds: 0,
    schedule: [] as ScheduleEntry[],
  });

  const normalizeScheduleEntriesForState = useCallback((entries: Array<Record<string, unknown>>): ScheduleEntry[] => (
    entries
      .filter((entry) => Array.isArray(entry.days))
      .map((entry) => {
        const allDay = Boolean(entry.allDay ?? entry.all_day);
        return {
          days: (entry.days as unknown[]).map(String),
          allDay,
          start: allDay ? null : String(entry.start || ''),
          end: allDay ? null : String(entry.end || ''),
        };
      })
  ), []);
  const [configIvrAudioFile, setConfigIvrAudioFile] = useState<File | null>(null);
  const [configIvrAudioDisplayName, setConfigIvrAudioDisplayName] = useState<string | null>(null);
  const [configIvrAudioDurationSeconds, setConfigIvrAudioDurationSeconds] = useState<number | null>(null);
  const [configIvrAudioPreviewUrl, setConfigIvrAudioPreviewUrl] = useState<string | null>(null);
  const [configReverseIvrAudioOptions, setConfigReverseIvrAudioOptions] = useState<ReverseIvrAudioOption[]>([]);
  const [configSelectedReverseIvrAudioId, setConfigSelectedReverseIvrAudioId] = useState<string>('');
  const [configSelectedReverseIvrAudioPreviewUrl, setConfigSelectedReverseIvrAudioPreviewUrl] = useState<string | null>(null);
  const [configReverseIvrAudioPreviewLoading, setConfigReverseIvrAudioPreviewLoading] = useState(false);
  const [configReverseIvrUseNewAudio, setConfigReverseIvrUseNewAudio] = useState(false);
  const [configIvrAudioError, setConfigIvrAudioError] = useState<string | null>(null);
  const [configIvrAudioFilenameSaving, setConfigIvrAudioFilenameSaving] = useState(false);
  const [configIvrAudioUploading, setConfigIvrAudioUploading] = useState(false);
  const [configIvrAudioUploadProgress, setConfigIvrAudioUploadProgress] = useState<number | null>(null);
  const [configIvrMaxAudioSeconds, setConfigIvrMaxAudioSeconds] = useState<number>(DEFAULT_IVR_MAX_AUDIO_SECONDS);
  const [configIvrMaxAudioFileSizeBytes, setConfigIvrMaxAudioFileSizeBytes] = useState<number | null>(null);
  const [configIvrDtmfRules, setConfigIvrDtmfRules] = useState<IvrDtmfRule[]>([
    createIvrRule({ digit: '1', status: 'converted' }),
  ]);
  const [notificationEmailsInput, setNotificationEmailsInput] = useState('');
  const [notificationEmailsError, setNotificationEmailsError] = useState<string | null>(null);

  // Results tab (contacts vs calls) - state lives in URL using `tab`
  const tabFromUrl = searchParams.get('tab');
  const activeResultsTab = tabFromUrl === 'calls' ? 'calls' : 'contacts';
  const isCallsTab = activeResultsTab === 'calls';
  const isImporting = campaign?.importStatus === 'processing' || campaign?.status === 'processing';
  const shouldEnableListRealtimeRefresh = Boolean(
    campaign && ['active', 'running', 'processing', 'paused'].includes(campaign.status),
  );

  useEffect(() => {
    if (tabFromUrl) {
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', 'contacts');
    setSearchParams(nextParams, { replace: true });
  }, [tabFromUrl, searchParams, setSearchParams]);

  const handleResultsTabChange = useCallback((_: SyntheticEvent, nextTab: string) => {
    if (nextTab === activeResultsTab) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    DETAIL_RESULTS_QUERY_KEYS.forEach((key) => nextParams.delete(key));
    nextParams.set('tab', nextTab);
    setSearchParams(nextParams);
  }, [activeResultsTab, searchParams, setSearchParams]);

  const contactListParams = useListSearchParams({
    pageParam: CONTACT_RESULTS_PARAM_KEYS.page,
    pageSizeParam: CONTACT_RESULTS_PARAM_KEYS.pageSize,
    orderingParam: CONTACT_RESULTS_PARAM_KEYS.ordering,
    filters: [CONTACT_RESULTS_PARAM_KEYS.status, CONTACT_RESULTS_PARAM_KEYS.search],
    defaultPage: 1,
    defaultPageSize: 20,
    defaultOrdering: '-updated_at',
  });

  const callsListParams = useListSearchParams({
    pageParam: CALL_RESULTS_PARAM_KEYS.page,
    pageSizeParam: CALL_RESULTS_PARAM_KEYS.pageSize,
    orderingParam: CALL_RESULTS_PARAM_KEYS.ordering,
    filters: [CALL_RESULTS_PARAM_KEYS.status, CALL_RESULTS_PARAM_KEYS.search],
    defaultPage: 1,
    defaultPageSize: 20,
    defaultOrdering: '-updated_at',
  });

  const {
    filters: contactFilters,
    page: contactPage,
    rowsPerPage: contactRowsPerPage,
    ordering: contactOrdering,
    setParams: setContactParams,
    setPage: setContactPage,
    setRowsPerPage: setContactRowsPerPage,
    getPageLink: getContactsPageLink,
  } = contactListParams;

  const {
    filters: callsFilters,
    page: callsPage,
    rowsPerPage: callsRowsPerPage,
    ordering: callsOrdering,
    setParams: setCallsParams,
    setPage: setCallsPage,
    setRowsPerPage: setCallsRowsPerPage,
    getPageLink: getCallsPageLink,
  } = callsListParams;

  const contactStatusParam = contactFilters[CONTACT_RESULTS_PARAM_KEYS.status] ?? '';
  const callsStatusParam = callsFilters[CALL_RESULTS_PARAM_KEYS.status] ?? '';
  const contactSearchParam = contactFilters[CONTACT_RESULTS_PARAM_KEYS.search] ?? '';
  const preCallStateParam = contactFilters[CONTACT_RESULTS_PARAM_KEYS.preCallState] ?? '';
  const callsSearchParam = callsFilters[CALL_RESULTS_PARAM_KEYS.search] ?? '';
  const [contactSearchInput, setContactSearchInput] = useState(contactSearchParam);
  const [callsSearchInput, setCallsSearchInput] = useState(callsSearchParam);
  const contactStatusClickTimeoutRef = useRef<number | null>(null);
  const callsStatusClickTimeoutRef = useRef<number | null>(null);

  const contactStatusFilters = useMemo(
    () =>
      contactStatusParam
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean),
    [contactStatusParam],
  );

  const preCallStateFilters = useMemo(
    () => preCallStateParam.split(',').map((value) => value.trim()).filter(Boolean),
    [preCallStateParam],
  );

  const callsStatusFilters = useMemo(
    () =>
      callsStatusParam
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean),
    [callsStatusParam],
  );

  const toggleContactStatus = (filter: string | string[], allVisibleFilters: string[]) => {
    const next = getNextInclusiveFilters(contactStatusFilters, filter, allVisibleFilters);
    setContactParams({ [CONTACT_RESULTS_PARAM_KEYS.status]: next.length ? next.join(',') : null }, { resetPage: true });
  };

  const togglePreCallState = (state: string) => {
    const next = preCallStateFilters.includes(state)
      ? preCallStateFilters.filter((value) => value !== state)
      : [...preCallStateFilters, state];
    setContactParams(
      { [CONTACT_RESULTS_PARAM_KEYS.preCallState]: next.length ? next.join(',') : null },
      { resetPage: true },
    );
  };

  const toggleCallsStatus = (filter: string | string[], allVisibleFilters: string[]) => {
    const next = getNextInclusiveFilters(callsStatusFilters, filter, allVisibleFilters);
    setCallsParams({ [CALL_RESULTS_PARAM_KEYS.status]: next.length ? next.join(',') : null }, { resetPage: true });
  };

  const isolateContactStatus = (filter: string | string[], allVisibleFilters: string[]) => {
    const next = getIsolatedInclusiveFilters(filter, allVisibleFilters);
    setContactParams({ [CONTACT_RESULTS_PARAM_KEYS.status]: next.length ? next.join(',') : null }, { resetPage: true });
  };

  const isolateCallsStatus = (filter: string | string[], allVisibleFilters: string[]) => {
    const next = getIsolatedInclusiveFilters(filter, allVisibleFilters);
    setCallsParams({ [CALL_RESULTS_PARAM_KEYS.status]: next.length ? next.join(',') : null }, { resetPage: true });
  };

  const scheduleStatusCardInteraction = (
    timeoutRef: MutableRefObject<number | null>,
    action: () => void,
  ) => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      action();
    }, STATUS_CARD_DOUBLE_CLICK_DELAY_MS);
  };

  const runStatusCardIsolation = (
    timeoutRef: MutableRefObject<number | null>,
    action: () => void,
  ) => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    action();
  };

  const renderStatusCardTooltip = (baseTooltip: string) => (
    <Box>
      <Typography variant="body2">{baseTooltip}</Typography>
      <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.9 }}>
        {t('pages:campaigns.detail.results.filterClickHint')}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>
        {t('pages:campaigns.detail.results.filterDoubleClickHint')}
      </Typography>
    </Box>
  );

  // Sync local search inputs when query params change externally
  useEffect(() => {
    setContactSearchInput(contactSearchParam);
  }, [contactSearchParam]);

  useEffect(() => {
    setCallsSearchInput(callsSearchParam);
  }, [callsSearchParam]);

  useEffect(() => () => {
    if (contactStatusClickTimeoutRef.current !== null) {
      window.clearTimeout(contactStatusClickTimeoutRef.current);
    }
    if (callsStatusClickTimeoutRef.current !== null) {
      window.clearTimeout(callsStatusClickTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (isImporting) {
        return;
      }
      const trimmed = contactSearchInput.trim();
      if (trimmed === contactSearchParam) {
        return;
      }
      setContactParams({ [CONTACT_RESULTS_PARAM_KEYS.search]: trimmed || undefined }, { resetPage: true });
    }, 300);
    return () => clearTimeout(handler);
  }, [contactSearchInput, contactSearchParam, setContactParams, isImporting]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (isImporting) {
        return;
      }
      const trimmed = callsSearchInput.trim();
      if (trimmed === callsSearchParam) {
        return;
      }
      setCallsParams({ [CALL_RESULTS_PARAM_KEYS.search]: trimmed || undefined }, { resetPage: true });
    }, 300);
    return () => clearTimeout(handler);
  }, [callsSearchInput, callsSearchParam, setCallsParams, isImporting]);

  // ============================================================================
  // Schedule Validation
  // ============================================================================

  // Helper to convert HH:mm to minutes for comparison
  const timeToMinutes = (time: string): number => {
    if (!time) return 0;
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };

  // Validate schedule entries
  const validateScheduleEdit = useCallback((): ScheduleError[] => {
    const errors: ScheduleError[] = [];

    if (configForm.schedule.length === 0) return errors;

    // Collect all time ranges per day for overlap checking
    const dayRanges: Record<string, { entryIndex: number; start: number; end: number; period: string }[]> = {};

    configForm.schedule.forEach((entry, entryIndex) => {
      // Check if days are selected
      if (entry.days.length === 0) {
        errors.push({
          entryIndex,
          type: 'no_days',
          message: t('pages:campaigns.create.settings.scheduleNoDays'),
        });
        return;
      }

      const isAllDay = Boolean(entry.allDay);
      const startMinutes = isAllDay ? 0 : timeToMinutes(entry.start || '');
      const endMinutes = isAllDay ? 24 * 60 : timeToMinutes(entry.end || '');

      // Check if start/end times are valid
      if (!isAllDay && (!entry.start || !entry.end)) {
        return; // Skip if times not filled yet
      }

      // Check if end time is after start time
      if (!isAllDay && endMinutes <= startMinutes) {
        errors.push({
          entryIndex,
          type: 'time_invalid',
          message: t('pages:campaigns.create.settings.scheduleTimeInvalid'),
        });
        return;
      }

      // Check each day against global limits
      for (const day of entry.days) {
        const globalDay = operatingHours[day];
        if (!globalDay?.enabled) {
          errors.push({
            entryIndex,
            type: 'outside_limits',
            message: t('pages:campaigns.create.settings.scheduleTenantClosed', {
              day: t(`common:days.${day}`),
            }),
          });
          continue;
        }

        if (isAllDay) {
          if (!globalDay.allDay) {
            errors.push({
              entryIndex,
              type: 'outside_limits',
              message: t('pages:campaigns.create.settings.scheduleAllDayOutsideLimits', {
                day: t(`common:days.${day}`),
              }),
            });
          }
        } else if (!globalDay.allDay && globalDay.start && globalDay.end) {
          const globalStartMinutes = timeToMinutes(globalDay.start);
          const globalEndMinutes = timeToMinutes(globalDay.end);

          // Check if campaign schedule is within global limits
          if (startMinutes < globalStartMinutes || endMinutes > globalEndMinutes) {
            errors.push({
              entryIndex,
              type: 'outside_limits',
              message: t('pages:campaigns.create.settings.scheduleOutsideLimits', {
                day: t(`common:days.${day}`),
                start: globalDay.start,
                end: globalDay.end,
              }),
            });
          }
        }

        // Collect for overlap checking
        if (!dayRanges[day]) dayRanges[day] = [];
        dayRanges[day].push({
          entryIndex,
          start: startMinutes,
          end: endMinutes,
          period: isAllDay
            ? t('pages:campaigns.create.settings.scheduleAllDayShort')
            : `${entry.start}-${entry.end}`,
        });
      }
    });

    // Check for overlaps between different entries on the same day
    Object.entries(dayRanges).forEach(([day, ranges]) => {
      for (let i = 0; i < ranges.length; i++) {
        for (let j = i + 1; j < ranges.length; j++) {
          const r1 = ranges[i];
          const r2 = ranges[j];

          // Check if ranges overlap
          if (r1.start < r2.end && r2.start < r1.end) {
            errors.push({
              entryIndex: r2.entryIndex,
              type: 'overlap',
              message: t('pages:campaigns.create.settings.scheduleOverlap', {
                day: t(`common:days.${day}`),
                period1: r1.period,
                period2: r2.period,
              }),
            });
          }
        }
      }
    });

    return errors;
  }, [configForm.schedule, operatingHours, t]);

  // Update schedule errors whenever entries change
  useEffect(() => {
    setScheduleErrors(validateScheduleEdit());
  }, [validateScheduleEdit]);

  // Get min/max times for a given entry based on selected days
  const getTimeConstraintsEdit = (entry: ScheduleEntry): { minTime: string | null; maxTime: string | null } => {
    if (entry.allDay) return { minTime: null, maxTime: null };
    if (entry.days.length === 0) return { minTime: null, maxTime: null };

    let minTime: string | null = null;
    let maxTime: string | null = null;

    // Find the most restrictive limits across all selected days
    for (const day of entry.days) {
      const globalDay = operatingHours[day];
      if (!globalDay?.enabled || globalDay.allDay || !globalDay.start || !globalDay.end) continue;

      if (!minTime || timeToMinutes(globalDay.start) > timeToMinutes(minTime)) {
        minTime = globalDay.start;
      }
      if (!maxTime || timeToMinutes(globalDay.end) < timeToMinutes(maxTime)) {
        maxTime = globalDay.end;
      }
    }

    return { minTime, maxTime };
  };

  // Get errors for a specific entry
  const getEntryErrorsEdit = (entryIndex: number): ScheduleError[] => {
    return scheduleErrors.filter(e => e.entryIndex === entryIndex);
  };

  // Generate default schedule entries from tenant operating hours
  const generateDefaultScheduleEntries = useCallback(
    (): ScheduleEntry[] => buildScheduleEntriesFromOperatingHours(operatingHours),
    [operatingHours]
  );

  // Reset schedule to tenant defaults
  const resetScheduleToDefaults = useCallback(() => {
    setConfigForm(prev => ({
      ...prev,
      schedule: generateDefaultScheduleEntries()
    }));
  }, [generateDefaultScheduleEntries]);

  // Save scroll position before navigation
  const resultsContainerRef = useRef<HTMLDivElement>(null);
  const scrollPositionsRef = useRef<{ contacts: number; calls: number }>({ contacts: 0, calls: 0 });

  // Restore scroll position when active tab changes
  useEffect(() => {
    if (resultsContainerRef.current) {
      const key = isCallsTab ? 'calls' : 'contacts';
      const savedPosition = scrollPositionsRef.current[key];
      if (savedPosition > 0) {
        setTimeout(() => {
          if (resultsContainerRef.current) {
            resultsContainerRef.current.scrollTop = savedPosition;
          }
        }, 100);
      }
    }
  }, [isCallsTab]);

  // Save scroll position on scroll
  const handleScroll = useCallback(() => {
    if (resultsContainerRef.current) {
      const key = isCallsTab ? 'calls' : 'contacts';
      scrollPositionsRef.current[key] = resultsContainerRef.current.scrollTop;
    }
  }, [isCallsTab]);

  const [totalContacts, setTotalContacts] = useState(0);

  // Calls state
  interface CallListItem {
    id: string;
    toNumber: string;
    fromNumber?: string;
    originLineName?: string | null;
    phoneLabel?: string | null;
    status: CallStatus;
    duration?: number;
    durationFormatted?: string;
    billedSeconds?: number;
    consumptionSegments?: CallConsumptionSegment[];
    activeSegmentId?: string | null;
    activeSegmentType?: CallSegmentType | null;
    segmentTotals?: CallSegmentTotals | null;
    createdAt: string;
    startedAt?: string;
    endedAt?: string;
    campaignId?: string;
    campaignName?: string;
    agentName?: string | null;
    agentId?: string;
    campaignContactId?: string;
    campaignContactDisplayName?: string; // Display name do contato
    campaignContactVariables?: Record<string, string>; // Variables do contato
    isConverted?: boolean;
    isOptout?: boolean;
    isMismatch?: boolean;
    estimatedCostUsd?: number;
    costCredits?: number;
    cost?: number;
  }
  const [calls, setCalls] = useState<CallListItem[]>([]);
  const [totalCalls, setTotalCalls] = useState(0);
  const [callsLoading, setCallsLoading] = useState(false);

  // Filters

  // Contacts viewer modal
  const [contactsModalOpen, setContactsModalOpen] = useState(false);

  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const isCampaignDebugEnabled = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return params.has('campaignDebug') || window.localStorage.getItem('ct_campaign_debug') === '1';
  }, []);
  const logCampaignDebug = useCallback(
    (message: string, data?: unknown) => {
      if (!isCampaignDebugEnabled) return;
      if (data !== undefined) {
        console.log(`[CampaignDetailPage] ${message}`, data);
        return;
      }
      console.log(`[CampaignDetailPage] ${message}`);
    },
    [isCampaignDebugEnabled],
  );

  // Confirmation dialog hook
  const { confirm, DialogComponent: ConfirmDialogComponent } = useConfirmDialog();
  const CAMPAIGN_DETAIL_SUMMARY_POLL_INTERVAL_MS = 45000;

  // =========================================================================
  // Data fetching functions (defined before WebSocket hook)
  // =========================================================================

  const fetchCampaignSummary = useCallback(async () => {
    try {
      const [campaignRes, statsRes] = await Promise.all([
        api.get(`/api/campaigns/${id}/`),
        api.get(`/api/campaigns/${id}/stats/`),
      ]);
      setCampaign(campaignRes.data);
      setStats(statsRes.data);
    } catch {
      setError(t('pages:campaigns.detail.errors.loadCampaign'));
    }
  }, [id, t]);

  const fetchCampaign = useCallback(async () => {
    await fetchCampaignSummary();
  }, [fetchCampaignSummary]);

  const fetchContacts = useCallback(async () => {
    try {
      const params: Record<string, unknown> = {
        page: contactPage + 1,
        page_size: contactRowsPerPage,
        ordering: contactOrdering || '-updated_at',
      };
      if (contactStatusFilters.length > 0) {
        params.status = contactStatusFilters;
      }
      if (contactSearchParam) {
        params.search = contactSearchParam;
      }
      if (preCallStateFilters.length > 0) {
        params.pre_call_state = preCallStateFilters;
      }

      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => queryParams.append(key, v));
        } else {
          queryParams.append(key, String(value));
        }
      });

      const res = await api.get(`/api/campaigns/${id}/contacts/?${queryParams.toString()}`);
      const data = res.data.results ?? res.data;
      setContacts(Array.isArray(data) ? data : []);
      setTotalContacts(res.data.count ?? data.length ?? 0);
    } catch (err) {
      console.error('Error loading contacts:', err);
    }
  }, [
    id,
    contactPage,
    contactRowsPerPage,
    contactStatusFilters,
    contactSearchParam,
    preCallStateFilters,
    contactOrdering,
  ]);

  const fetchScripts = useCallback(async () => {
    try {
      const res = await api.get(`/api/campaigns/${id}/scripts/`);
      setScripts(res.data || []);
    } catch (err) {
      console.error('Error loading scripts:', err);
    }
  }, [id]);

  const fetchPreCallStatus = useCallback(async () => {
    try {
      const res = await api.get(`/api/campaigns/${id}/pre-call-status/`);
      setPreCallStatus(res.data);
    } catch (err) {
      console.error('Error loading pre-call status:', err);
      setPreCallStatus(null);
    }
  }, [id]);

  const fetchCalls = useCallback(async () => {
    if (!id) return;
    setCallsLoading(true);
    try {
      const params: Record<string, unknown> = {
        campaign: id,
        page: callsPage + 1,
        page_size: callsRowsPerPage,
        ordering: callsOrdering || '-updated_at',
      };
      if (callsStatusFilters.length > 0) {
        params.status = callsStatusFilters;
      }
      if (callsSearchParam) {
        params.to_number = callsSearchParam;
      }

      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => queryParams.append(key, v));
        } else {
          queryParams.append(key, String(value));
        }
      });

      const res = await api.get(`/api/calls/?${queryParams.toString()}`);
      const data = res.data.results ?? res.data;
      setCalls(Array.isArray(data) ? data : []);
      setTotalCalls(res.data.count ?? data.length ?? 0);
    } catch (err) {
      console.error('Error loading calls:', err);
      setCalls([]);
      setTotalCalls(0);
    } finally {
      setCallsLoading(false);
    }
  }, [
    id,
    callsPage,
    callsRowsPerPage,
    callsStatusFilters,
    callsSearchParam,
    callsOrdering,
  ]);

  const mergeCampaignDerivedStats = useCallback((event: Partial<Campaign>) => {
    setStats((previous) => {
      if (!previous) return previous;
      const source = event as Record<string, unknown>;
      const next = { ...previous };
      let changed = false;
      const statKeys = [
        'totalContacts',
        'pendingContacts',
        'inProgressContacts',
        'reachedContacts',
        'optoutContacts',
        'mismatchContacts',
        'exhaustedContacts',
        'convertedContacts',
        'noValidPhonesContacts',
        'totalCalls',
        'connectedCalls',
        'conversionEligibleCalls',
        'failedCalls',
        'convertedCalls',
      ];
      statKeys.forEach((key) => {
        if (source[key] !== undefined) {
          (next as Record<string, unknown>)[key] = source[key];
          changed = true;
        }
      });
      return changed ? next : previous;
    });
  }, []);

  // Manual refresh keeps parity with explicit user action.
  const refreshAllData = useCallback(() => {
    fetchCampaign();
    fetchContacts();
  }, [fetchCampaign, fetchContacts]);

  const campaignInvalidationRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleCampaignInvalidationRefresh = useCallback(() => {
    if (campaignInvalidationRefreshTimerRef.current) {
      return;
    }
    campaignInvalidationRefreshTimerRef.current = setTimeout(() => {
      campaignInvalidationRefreshTimerRef.current = null;
      fetchCampaign();
    }, 750);
  }, [fetchCampaign]);

  // =========================================================================
  // WebSocket for real-time updates
  // =========================================================================

  const { isConnected: wsConnected, importProgress, lastEvent } = useCampaignWebSocket(id, {
    onImportComplete: (event) => {
      logCampaignDebug('onImportComplete callback', {
        event,
        shouldOpenContacts: event.importStatus === 'completed',
      });
      if (event.importStatus === 'completed') {
        setContactsModalOpen(true);
      }
    },
    onCampaignUpdated: (event) => {
      logCampaignDebug('onCampaignUpdated callback', {
        event,
        previousCampaignStatus: campaign?.status,
      });
      setCampaign(prev => (prev ? { ...prev, ...event } : prev));
      mergeCampaignDerivedStats(event);
    },
    onCampaignInvalidated: (event) => {
      logCampaignDebug('onCampaignInvalidated callback', { event });
      scheduleCampaignInvalidationRefresh();
    },
  });

  const persistedImportProgress: ImportProgressData | null = useMemo(
    () => (
      campaign?.importStatus === 'processing'
        ? {
            progress: campaign.importProgress ?? 0,
            processedRows: campaign.importProcessedRows ?? 0,
            totalRows: campaign.importTotalRows ?? 0,
            message: campaign.importMessage ?? undefined,
            estimatedRemainingSeconds: Number(
              (campaign.importStats as Record<string, unknown> | undefined)?.estimatedRemainingSeconds
                ?? (campaign.importStats as Record<string, unknown> | undefined)?.estimated_remaining_seconds,
            ) || undefined,
            phase: campaign.importPhase ?? undefined,
          }
        : null
    ),
    [campaign],
  );

  const effectiveImportProgress: ImportProgressData | null = useMemo(() => {
    if (campaign?.importStatus === 'completed' || campaign?.importStatus === 'failed') {
      return null;
    }
    return pickMostAdvancedImportProgress(importProgress, persistedImportProgress);
  }, [campaign?.importStatus, importProgress, persistedImportProgress]);

  const { scheduleRefresh: scheduleListRefresh } = useCampaignRealtimeListRefresh({
    campaignId: id,
    wsConnected: wsConnected && shouldEnableListRealtimeRefresh,
    isCallsTab,
    onRefreshContacts: fetchContacts,
    onRefreshCalls: fetchCalls,
  });

  useEffect(() => {
    if (!lastEvent) {
      return;
    }
    scheduleListRefresh({ contacts: true, calls: true });
  }, [lastEvent, scheduleListRefresh]);

  useEffect(() => () => {
    if (campaignInvalidationRefreshTimerRef.current) {
      clearTimeout(campaignInvalidationRefreshTimerRef.current);
    }
  }, []);

  // Auto-open contacts modal if showContacts query param is present
  // But only if the campaign is NOT processing (so user can see import progress first)
  useEffect(() => {
    logCampaignDebug('showContacts effect check', {
      showContacts: searchParams.get('showContacts'),
      hasCampaign: Boolean(campaign),
      isImporting,
    });
    if (searchParams.get('showContacts') === 'true' && campaign) {
      // If processing, don't open modal - let user see the progress on main page
      if (!isImporting) {
        logCampaignDebug('opening contacts modal from showContacts query param');
        setContactsModalOpen(true);
      }
      // Remove the query param from URL
      if (!isImporting) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('showContacts');
        setSearchParams(nextParams, { replace: true });
      }
    }
  }, [searchParams, setSearchParams, campaign, isImporting, logCampaignDebug]);

  // Initial load - only once when component mounts or id changes
  useEffect(() => {
    setLoading(true);
    Promise.all([fetchCampaign(), fetchScripts(), fetchPreCallStatus()])
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch contacts when filters change
  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Fetch calls when the Calls tab is active and filters change
  useEffect(() => {
    if (isCallsTab) {
      fetchCalls();
    }
  }, [isCallsTab, fetchCalls]);

  useEffect(() => {
    if (!campaign || !wsConnected) {
      return;
    }
    if (!['active', 'processing', 'paused'].includes(campaign.status)) {
      return;
    }

    const timer = setInterval(() => {
      fetchCampaign();
      scheduleListRefresh({ contacts: true, calls: true }, { incrementBadge: false });
    }, CAMPAIGN_DETAIL_SUMMARY_POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [campaign, fetchCampaign, scheduleListRefresh, wsConnected, CAMPAIGN_DETAIL_SUMMARY_POLL_INTERVAL_MS]);

  const handleAction = async (action: string) => {
    try {
      const res = await api.post(`/api/campaigns/${id}/${action}/`);
      fetchCampaign();
      fetchContacts();
      return res.data;
    } catch (err: unknown) {
      const conflict = getCampaignConcurrencyConflict(err);
      if (conflict) {
        setConcurrencyConflict(conflict);
        return null;
      }
      const error = err as { response?: { data?: { error?: string } } };
      setError(error?.response?.data?.error || t('pages:campaigns.detail.errors.actionFailed', { action }));
      return null;
    }
  };

  const handleOpenExportModal = () => {
    setExportModalOpen(true);
  };

  const resetCloneDraft = useCallback(() => {
    if (cloneIvrAudioPreviewUrl) {
      URL.revokeObjectURL(cloneIvrAudioPreviewUrl);
    }
    setCloneFilters(DEFAULT_CLONE_FILTERS);
    setCloneHistoricalImportFilters(DEFAULT_CLONE_HISTORICAL_IMPORT_FILTERS);
    setCloneContactSource('campaign_contacts');
    setCloneContactsFile(null);
    setCloneResourcesLoading(false);
    setCloneResourcesError(null);
    setCloneSubmitting(false);
    setCloneSubmitPhase(null);
    setCloneUploadProgress(null);
    setCloneReverseIvrAudioOptions([]);
    setCloneIvrAudioSource('keep');
    setCloneSelectedReverseIvrAudioId('');
    setCloneIvrAudioFile(null);
    setCloneIvrAudioDurationSeconds(null);
    setCloneIvrAudioPreviewUrl(null);
    setCloneIvrAudioError(null);
    setCloneIvrMaxAudioSeconds(DEFAULT_IVR_MAX_AUDIO_SECONDS);
  }, [cloneIvrAudioPreviewUrl]);

  const handleCloneContactsFileSelect = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] ?? null;
    setCloneContactsFile(selectedFile);
    setCloneResourcesError(null);
    event.target.value = '';
  }, []);

  const handleClone = async () => {
    if (!campaign) {
      return;
    }

    const isReverseIvrClone = (campaign.campaignType ?? 'ai') === 'reverse_ivr';
    try {
      setCloneSubmitting(true);
      setCloneUploadProgress(null);
      setCloneResourcesError(null);
      setCloneIvrAudioError(null);

      let contactsFileId: string | null = null;
      let ivrAudioFileId: string | null = null;

      if (cloneContactSource === 'uploaded_file') {
        if (!cloneContactsFile) {
          setCloneResourcesError(t('pages:campaigns.detail.clone.contactsFileRequired'));
          return;
        }
        setCloneSubmitPhase('uploading_contacts');
        contactsFileId = await uploadMediaFileDirect({
          file: cloneContactsFile,
          fileType: 'contacts',
          onUploadProgress: (percentCompleted) => {
            setCloneUploadProgress(percentCompleted);
          },
        });
      }

      if (isReverseIvrClone) {
        if (cloneIvrAudioSource === 'existing' && !cloneSelectedReverseIvrAudioId) {
          setCloneIvrAudioError(t('pages:campaigns.detail.clone.ivrAudioExistingRequired'));
          return;
        }
        if (cloneIvrAudioSource === 'uploaded') {
          if (!cloneIvrAudioFile) {
            setCloneIvrAudioError(t('pages:campaigns.create.errors.ivrAudioRequired'));
            return;
          }
          if (
            cloneIvrAudioDurationSeconds !== null &&
            cloneIvrAudioDurationSeconds > cloneIvrMaxAudioSeconds
          ) {
            setCloneIvrAudioError(
              t('pages:campaigns.create.errors.ivrAudioTooLong', {
                maxSeconds: cloneIvrMaxAudioSeconds,
              }),
            );
            return;
          }
          setCloneSubmitPhase('uploading_audio');
          ivrAudioFileId = await uploadMediaFileDirect({
            file: cloneIvrAudioFile,
            fileType: 'reverse_ivr_audio',
            metadata: cloneIvrAudioDurationSeconds !== null
              ? { duration_seconds: Number(cloneIvrAudioDurationSeconds.toFixed(3)) }
              : {},
            onUploadProgress: (percentCompleted) => {
              setCloneUploadProgress(percentCompleted);
            },
          });
        } else if (cloneIvrAudioSource === 'existing') {
          ivrAudioFileId = cloneSelectedReverseIvrAudioId;
        }
      }

      setCloneSubmitPhase('cloning');
      setCloneUploadProgress(null);

      const payload: Record<string, unknown> = {
        name: cloneName || `${campaign.name} (Clone)`,
        contact_source: cloneContactSource,
        copy_settings: true,
      };

      if (cloneContactSource === 'campaign_contacts') {
        payload.filters = {
          exclude_converted: cloneFilters.excludeConverted,
          exclude_optout: cloneFilters.excludeOptout,
          exclude_reached: cloneFilters.excludeReached,
          exclude_no_valid_phones: cloneFilters.excludeNoValidPhones,
          exclude_has_dtmf: cloneFilters.excludeHasDtmf,
          exclude_heard_audio_to_end: cloneFilters.excludeHeardAudioToEnd,
          exclude_invalid_phones: cloneFilters.excludeInvalidPhones,
          exclude_mismatch_phones: cloneFilters.excludeMismatchPhones,
        };
      } else if (contactsFileId) {
        payload.contacts_file_id = contactsFileId;
        payload.remove_previously_converted_contacts = cloneHistoricalImportFilters.removePreviouslyConvertedContacts;
        payload.remove_previously_reached_contacts = cloneHistoricalImportFilters.removePreviouslyReachedContacts;
        payload.remove_previously_optout_contacts = cloneHistoricalImportFilters.removePreviouslyOptoutContacts;
      }

      if (isReverseIvrClone) {
        payload.ivr_audio_source = cloneIvrAudioSource;
        if (ivrAudioFileId) {
          payload.ivr_audio_file_id = ivrAudioFileId;
        }
      }

      const res = await api.post(`/api/campaigns/${id}/clone/`, payload);
      setCloneDialogOpen(false);
      resetCloneDraft();

      const clonedCampaignId = res.data?.campaignId ?? res.data?.campaign_id;
      if (clonedCampaignId) {
        navigate(`/campaigns/${clonedCampaignId}`);
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      const errorMessage = error?.response?.data?.error || t('pages:campaigns.detail.errors.cloneCampaign');
      setCloneResourcesError(errorMessage);
      setError(errorMessage);
    } finally {
      setCloneSubmitting(false);
      setCloneSubmitPhase(null);
      setCloneUploadProgress(null);
    }
  };

  // Format display name - if it looks like a phone number, format it
  const formatDisplayName = (name: string) => {
    // Check if it looks like a phone number (starts with + or is mostly digits)
    const cleaned = name.replace(/[\s\-()]/g, '');
    if (cleaned.startsWith('+') || /^\d{8,}$/.test(cleaned)) {
      return formatPhone(name);
    }
    return name;
  };

  // Format variables for tooltip display - similar to Contact Information format
  const formatVariablesTooltip = (variables?: Record<string, string>): React.ReactNode => {
    if (!variables || Object.keys(variables).length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t('pages:campaigns.detail.results.noVariables')}
        </Typography>
      );
    }

    return (
      <Box sx={{ minWidth: 300, maxWidth: 600 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1.5, columnGap: 2 }}>
          {Object.entries(variables).map(([key, value]) => (
            <Box key={key}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5 }}
              >
                {key}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {value || '-'}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const handleCancelCampaign = async () => {
    const confirmed = await confirm({
      title: t('pages:campaigns.cancelCampaign.title'),
      message: t('pages:campaigns.cancelCampaign.message'),
      type: 'warning',
      icon: <Cancel color="error" />,
      confirmIcon: <Cancel />,
      confirmText: t('pages:campaigns.cancelCampaign.confirm'),
    });
    if (confirmed) {
      handleAction('cancel');
    }
  };

  const handleCloneCampaign = async () => {
    if (!campaign) {
      return;
    }

    resetCloneDraft();
    setCloneName(`${campaign.name} (Clone)`);
    setCloneHistoricalImportFilters({
      removePreviouslyConvertedContacts: Boolean(campaign.removePreviouslyConvertedContacts),
      removePreviouslyReachedContacts: Boolean(campaign.removePreviouslyReachedContacts),
      removePreviouslyOptoutContacts: Boolean(campaign.removePreviouslyOptoutContacts),
    });
    setCloneDialogOpen(true);

    if ((campaign.campaignType ?? 'ai') !== 'reverse_ivr') {
      return;
    }

    setCloneResourcesLoading(true);
    try {
      const bootstrapResponse = await api.get('/api/campaigns/bootstrap/');
      const bootstrap = bootstrapResponse.data || {};
      const reverseIvrAudioOptionsRaw = (
        bootstrap?.resources?.reverseIvrAudioOptions
        ?? bootstrap?.resources?.reverse_ivr_audio_options
        ?? []
      );
      const reverseIvrAudioOptionsData: ReverseIvrAudioOption[] = Array.isArray(reverseIvrAudioOptionsRaw)
        ? reverseIvrAudioOptionsRaw.map((option) => normalizeReverseIvrAudioOption(option))
        : [];
      const normalizedReverseIvrAudioOptions = [...reverseIvrAudioOptionsData];
      if (
        campaign.ivrAudioFileId
        && !normalizedReverseIvrAudioOptions.some((option) => option.id === campaign.ivrAudioFileId)
      ) {
        normalizedReverseIvrAudioOptions.unshift({
          id: campaign.ivrAudioFileId,
          name: campaign.ivrAudioFileId,
          displayName: campaign.ivrAudioFileId,
          originalFilename: campaign.ivrAudioFileId,
        });
      }
      const rawIvrMaxAudioSeconds = Number(
        bootstrap?.limits?.ivr?.ivrMaxAudioSeconds
        ?? bootstrap?.limits?.ivr?.ivr_max_audio_seconds
      );
      setCloneIvrMaxAudioSeconds(
        Number.isFinite(rawIvrMaxAudioSeconds) && rawIvrMaxAudioSeconds > 0
          ? Math.floor(rawIvrMaxAudioSeconds)
          : DEFAULT_IVR_MAX_AUDIO_SECONDS,
      );
      setCloneReverseIvrAudioOptions(normalizedReverseIvrAudioOptions);
      setCloneSelectedReverseIvrAudioId(campaign.ivrAudioFileId || '');
    } catch (cloneResourcesErr) {
      console.error('Error loading clone resources:', cloneResourcesErr);
      setCloneResourcesError(t('pages:campaigns.detail.clone.resourcesLoadFailed'));
      if (campaign.ivrAudioFileId) {
        setCloneReverseIvrAudioOptions([{ id: campaign.ivrAudioFileId, name: campaign.ivrAudioFileId }]);
        setCloneSelectedReverseIvrAudioId(campaign.ivrAudioFileId);
      }
    } finally {
      setCloneResourcesLoading(false);
    }
  };

  // Configuration modal handlers
  const canEditCampaign = campaign && ['draft', 'ready', 'paused'].includes(campaign.status);
  const hasFiniteMaxConcurrent = maxAllowedConcurrent !== null && maxAllowedConcurrent > 0;
  const selectedConfigReverseIvrAudioOption = configReverseIvrAudioOptions.find(
    (audioOption) => audioOption.id === configSelectedReverseIvrAudioId,
  ) || null;
  const configIvrDtmfValidationErrorKey = useMemo(
    () => getIvrDtmfValidationErrorKey(configIvrDtmfRules),
    [configIvrDtmfRules],
  );
  const readonlyReverseIvrAudioOption = (
    campaign?.ivrAudioFileId
      ? (configReverseIvrAudioOptions.find((audioOption) => audioOption.id === campaign.ivrAudioFileId) || null)
      : null
  );
  const hasConfigSelectedReverseIvrAudio = configReverseIvrUseNewAudio
    ? Boolean(configIvrAudioFile)
    : Boolean(configSelectedReverseIvrAudioId);
  const readonlyReverseIvrAudioName = readonlyReverseIvrAudioOption
    ? getReverseIvrAudioDisplayName(readonlyReverseIvrAudioOption)
    : (campaign?.ivrAudioFileId || '—');
  const readonlyReverseIvrAudioDuration = readonlyReverseIvrAudioOption?.durationSeconds;
  const readonlyReverseIvrAudioSizeMb = readonlyReverseIvrAudioOption?.fileSize
    ? formatDecimal(readonlyReverseIvrAudioOption.fileSize / 1024 / 1024, 2)
    : null;
  const isCloneReverseIvrCampaign = (campaign?.campaignType ?? 'ai') === 'reverse_ivr';
  const selectedCloneReverseIvrAudioOption = cloneReverseIvrAudioOptions.find(
    (audioOption) => audioOption.id === cloneSelectedReverseIvrAudioId,
  ) || null;
  const cloneSubmitDisabled = cloneSubmitting
    || (cloneContactSource === 'uploaded_file' && !cloneContactsFile)
    || (
      isCloneReverseIvrCampaign
      && cloneIvrAudioSource === 'existing'
      && !cloneSelectedReverseIvrAudioId
    )
    || (
      isCloneReverseIvrCampaign
      && cloneIvrAudioSource === 'uploaded'
      && !cloneIvrAudioFile
    );
  const selectedConfigReverseIvrAudioPreview = configReverseIvrUseNewAudio
    ? configIvrAudioPreviewUrl
    : (
      configSelectedReverseIvrAudioPreviewUrl
      || (
        campaign
        && configSelectedReverseIvrAudioId === campaign.ivrAudioFileId
        ? campaign.ivrAudioFileUrl
        : null
      )
    );

  const clampMaxConcurrent = useCallback((value: number) => {
    const normalized = Math.max(1, value || 1);
    // If maxAllowedConcurrent is null or 0, clamp to 1 (minimum)
    if (maxAllowedConcurrent === null || maxAllowedConcurrent === 0) {
      return 1;
    }
    return Math.min(normalized, maxAllowedConcurrent);
  }, [maxAllowedConcurrent]);

  const validateNotificationEmails = (value: string) => {
    const emails = parseNotificationEmails(value);
    if (emails.length === 0) {
      return { emails: [], error: null };
    }
    const invalidEmails = emails.filter((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    if (invalidEmails.length > 0) {
      return {
        emails,
        error: t('pages:campaigns.detail.config.notificationEmailsInvalid', { count: invalidEmails.length }),
      };
    }
    return { emails, error: null };
  };

  const handleNotificationEmailsChange = (value: string) => {
    setNotificationEmailsInput(value);
    const { emails, error: validationError } = validateNotificationEmails(value);
    setNotificationEmailsError(validationError);
    setConfigForm((prev) => ({ ...prev, notificationEmails: emails }));
  };

  const resetConfigIvrAudioDraft = useCallback(() => {
    if (configIvrAudioPreviewUrl) {
      URL.revokeObjectURL(configIvrAudioPreviewUrl);
    }
    setConfigIvrAudioFile(null);
    setConfigIvrAudioDisplayName(null);
    setConfigIvrAudioDurationSeconds(null);
    setConfigIvrAudioPreviewUrl(null);
    setConfigReverseIvrAudioOptions([]);
    setConfigSelectedReverseIvrAudioId('');
    setConfigSelectedReverseIvrAudioPreviewUrl(null);
    setConfigReverseIvrAudioPreviewLoading(false);
    setConfigReverseIvrUseNewAudio(false);
    setConfigIvrAudioError(null);
    setConfigIvrAudioFilenameSaving(false);
    setConfigIvrAudioUploading(false);
    setConfigIvrAudioUploadProgress(null);
    setConfigIvrMaxAudioFileSizeBytes(null);
  }, [configIvrAudioPreviewUrl]);

  const resolveAudioDurationSeconds = useCallback((file: File): Promise<number | null> => (
    new Promise((resolve) => {
      const tempUrl = URL.createObjectURL(file);
      const audio = new Audio(tempUrl);
      audio.onloadedmetadata = () => {
        const duration = Number.isFinite(audio.duration) ? Number(audio.duration) : null;
        URL.revokeObjectURL(tempUrl);
        resolve(duration);
      };
      audio.onerror = () => {
        URL.revokeObjectURL(tempUrl);
        resolve(null);
      };
    })
  ), []);

  const handleConfigIvrAudioSelect = useCallback(async (selectedFile: File) => {
    setConfigIvrAudioError(null);
    const durationSeconds = await resolveAudioDurationSeconds(selectedFile);
    if (durationSeconds !== null && durationSeconds > configIvrMaxAudioSeconds) {
      setConfigIvrAudioError(
        t('pages:campaigns.create.errors.ivrAudioTooLong', {
          maxSeconds: configIvrMaxAudioSeconds,
        }),
      );
      return;
    }

    if (configIvrAudioPreviewUrl) {
      URL.revokeObjectURL(configIvrAudioPreviewUrl);
    }

    setConfigIvrAudioFile(selectedFile);
    setConfigIvrAudioDisplayName(selectedFile.name);
    setConfigIvrAudioDurationSeconds(durationSeconds);
    setConfigIvrAudioPreviewUrl(URL.createObjectURL(selectedFile));
    setConfigIvrAudioError(null);
  }, [configIvrAudioPreviewUrl, configIvrMaxAudioSeconds, resolveAudioDurationSeconds, t]);

  const handleConfigIvrAudioDisplayNameCommit = useCallback(async (nextDisplayName: string) => {
    if (configReverseIvrUseNewAudio && configIvrAudioFile) {
      setConfigIvrAudioDisplayName(nextDisplayName);
      return;
    }

    if (!configSelectedReverseIvrAudioId) {
      return;
    }

    setConfigIvrAudioFilenameSaving(true);
    setConfigIvrAudioError(null);
    try {
      const response = await api.post(`/api/media/${configSelectedReverseIvrAudioId}/rename/`, {
        displayName: nextDisplayName,
      });
      const updatedDisplayName = String(
        response.data?.displayName
        ?? response.data?.display_name
        ?? nextDisplayName,
      ).trim();
      const updatedOriginalFilename = response.data?.originalFilename
        ?? response.data?.original_filename
        ?? null;
      setConfigReverseIvrAudioOptions((prev) => prev.map((option) => (
        option.id === configSelectedReverseIvrAudioId
          ? normalizeReverseIvrAudioOption({
            ...option,
            displayName: updatedDisplayName,
            name: updatedDisplayName,
            originalFilename: updatedOriginalFilename,
          })
          : option
      )));
    } catch (renameError) {
      console.error('Failed to rename campaign IVR audio:', renameError);
      setConfigIvrAudioError(t('pages:campaigns.create.errors.ivrAudioRenameFailed'));
    } finally {
      setConfigIvrAudioFilenameSaving(false);
    }
  }, [
    configIvrAudioFile,
    configReverseIvrUseNewAudio,
    configSelectedReverseIvrAudioId,
    t,
  ]);

  const handleCloneIvrAudioSelect = useCallback(async (selectedFile: File) => {
    setCloneIvrAudioError(null);
    const durationSeconds = await resolveAudioDurationSeconds(selectedFile);
    if (durationSeconds !== null && durationSeconds > cloneIvrMaxAudioSeconds) {
      setCloneIvrAudioError(
        t('pages:campaigns.create.errors.ivrAudioTooLong', {
          maxSeconds: cloneIvrMaxAudioSeconds,
        }),
      );
      return;
    }

    if (cloneIvrAudioPreviewUrl) {
      URL.revokeObjectURL(cloneIvrAudioPreviewUrl);
    }

    setCloneIvrAudioFile(selectedFile);
    setCloneIvrAudioDurationSeconds(durationSeconds);
    setCloneIvrAudioPreviewUrl(URL.createObjectURL(selectedFile));
    setCloneIvrAudioError(null);
  }, [cloneIvrAudioPreviewUrl, cloneIvrMaxAudioSeconds, resolveAudioDurationSeconds, t]);

  useEffect(() => {
    if (configReverseIvrUseNewAudio) {
      setConfigSelectedReverseIvrAudioId('');
      setConfigSelectedReverseIvrAudioPreviewUrl(null);
      return;
    }
    setConfigIvrAudioFile(null);
    setConfigIvrAudioDurationSeconds(null);
    setConfigIvrAudioError(null);
    if (configIvrAudioPreviewUrl) {
      URL.revokeObjectURL(configIvrAudioPreviewUrl);
      setConfigIvrAudioPreviewUrl(null);
    }
  }, [configIvrAudioPreviewUrl, configReverseIvrUseNewAudio]);

  useEffect(() => {
    if (
      !configModalOpen
      || configReverseIvrUseNewAudio
      || !configSelectedReverseIvrAudioId
    ) {
      setConfigSelectedReverseIvrAudioPreviewUrl(null);
      setConfigReverseIvrAudioPreviewLoading(false);
      return;
    }

    let isMounted = true;
    setConfigReverseIvrAudioPreviewLoading(true);
    api.get(`/api/media/${configSelectedReverseIvrAudioId}/`)
      .then((response) => {
        if (!isMounted) {
          return;
        }
        setConfigSelectedReverseIvrAudioPreviewUrl(extractMediaPreviewUrl(response?.data));
      })
      .catch((previewError) => {
        if (!isMounted) {
          return;
        }
        console.error('Error loading reverse IVR campaign audio preview:', previewError);
        setConfigSelectedReverseIvrAudioPreviewUrl(null);
      })
      .finally(() => {
        if (!isMounted) {
          return;
        }
        setConfigReverseIvrAudioPreviewLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [configModalOpen, configReverseIvrUseNewAudio, configSelectedReverseIvrAudioId]);


  const handleOpenConfigModal = async () => {
    resetConfigIvrAudioDraft();
    setConfigIvrMaxAudioSeconds(DEFAULT_IVR_MAX_AUDIO_SECONDS);
    setResourcesError(null);
    if (campaign) {
      setResourcesLoading(true);
      // Fetch consolidated campaign bootstrap payload.
      try {
        const campaignTypeParam = ((campaign as Campaign).campaignType ?? 'ai') as 'ai' | 'reverse_ivr';
        const bootstrapResponse = await api.get('/api/campaigns/bootstrap/');
        const bootstrap = bootstrapResponse.data || {};
        const maxAllowedByType =
          bootstrap?.limits?.maxAllowedConcurrent
          ?? bootstrap?.limits?.max_allowed_concurrent
          ?? {};
        const maxAllowedByTypeNormalized =
          maxAllowedByType as Record<string, unknown>;
        const rawMaxAllowed =
          campaignTypeParam === 'reverse_ivr'
            ? (maxAllowedByTypeNormalized.reverseIvr
              ?? maxAllowedByTypeNormalized.reverse_ivr
              ?? null)
            : (maxAllowedByTypeNormalized[campaignTypeParam] ?? null);
        const maxAllowed = Number.isFinite(Number(rawMaxAllowed)) ? Number(rawMaxAllowed) : null;
        setMaxAllowedConcurrent(maxAllowed);

        setOperatingHours(
          normalizeTenantOperatingHours(
            bootstrap?.defaults?.operatingHours ?? bootstrap?.defaults?.operating_hours
          )
        );
        const rawIvrMaxAudioSeconds = Number(
          bootstrap?.limits?.ivr?.ivrMaxAudioSeconds
          ?? bootstrap?.limits?.ivr?.ivr_max_audio_seconds
        );
        const rawIvrMaxAudioFileSizeBytes = Number(
          bootstrap?.limits?.ivr?.ivrMaxAudioFileSizeBytes
          ?? bootstrap?.limits?.ivr?.ivr_max_audio_file_size_bytes
        );
        setConfigIvrMaxAudioSeconds(
          Number.isFinite(rawIvrMaxAudioSeconds) && rawIvrMaxAudioSeconds > 0
            ? Math.floor(rawIvrMaxAudioSeconds)
            : DEFAULT_IVR_MAX_AUDIO_SECONDS
        );
        setConfigIvrMaxAudioFileSizeBytes(
          Number.isFinite(rawIvrMaxAudioFileSizeBytes) && rawIvrMaxAudioFileSizeBytes > 0
            ? Math.floor(rawIvrMaxAudioFileSizeBytes)
            : null
        );

        const reverseIvrAudioOptionsRaw = (
          bootstrap?.resources?.reverseIvrAudioOptions
          ?? bootstrap?.resources?.reverse_ivr_audio_options
          ?? []
        );
        const reverseIvrAudioOptionsData: ReverseIvrAudioOption[] = Array.isArray(reverseIvrAudioOptionsRaw)
          ? reverseIvrAudioOptionsRaw.map((option) => normalizeReverseIvrAudioOption(option))
          : [];
        const normalizedReverseIvrAudioOptions = [...reverseIvrAudioOptionsData];
        if (
          campaignTypeParam === 'reverse_ivr'
          && campaign.ivrAudioFileId
          && !normalizedReverseIvrAudioOptions.some((option) => option.id === campaign.ivrAudioFileId)
        ) {
          normalizedReverseIvrAudioOptions.unshift({
            id: campaign.ivrAudioFileId,
            name: campaign.ivrAudioFileId,
            displayName: campaign.ivrAudioFileId,
            originalFilename: campaign.ivrAudioFileId,
          });
        }
        setConfigReverseIvrAudioOptions(normalizedReverseIvrAudioOptions);
        setConfigSelectedReverseIvrAudioId(() => {
          if (campaignTypeParam !== 'reverse_ivr') {
            return '';
          }
          if (campaign.ivrAudioFileId) {
            return campaign.ivrAudioFileId;
          }
          if (normalizedReverseIvrAudioOptions.length === 1) {
            return normalizedReverseIvrAudioOptions[0].id;
          }
          return '';
        });
        setConfigSelectedReverseIvrAudioPreviewUrl(null);
        setConfigReverseIvrUseNewAudio(false);
        setConfigIvrDtmfRules(parseIvrDtmfRules(campaign.ivrDtmfMap));

        const providerResources =
          bootstrap?.resources?.providerResources
          ?? bootstrap?.resources?.provider_resources
          ?? {};
        setProviderResourcesByProvider(providerResources);
        if (campaignTypeParam === 'ai' && campaign.aiAgentProviderId) {
          const selectedProviderResources = providerResources[campaign.aiAgentProviderId];
          setAvailableAgents(selectedProviderResources?.agents ?? []);
          setConfigSelectedAgentId(campaign.aiAgentId || '');
        } else {
          setAvailableAgents([]);
          setConfigSelectedAgentId('');
        }

        const scriptsPayload = Array.isArray(bootstrap?.resources?.scripts)
          ? bootstrap?.resources?.scripts
          : [];
        setAvailableScripts(scriptsPayload);
        setConfigSelectedScriptIds((scripts || []).map((script) => script.id));

        const transferPayload = bootstrap?.transfer ?? {};
        const telephonyEnabled = Boolean(
          transferPayload.telephonyPackEnabled ?? transferPayload.telephony_pack_enabled
        );
        setTransferTelephonyPackEnabled(telephonyEnabled);
        setTransferUsers(Array.isArray(transferPayload.users) ? transferPayload.users : []);
        setTransferGroups(Array.isArray(transferPayload.groups) ? transferPayload.groups : []);
        setConfigTransferTargetType(
          (campaign.transferTargetType ?? '') as CampaignTransferTargetType | ''
        );
        setConfigTransferTargetId(campaign.transferTargetId ? String(campaign.transferTargetId) : '');
        setConfigSuspendWhenNoTransferOperatorAvailable(
          campaign.suspendWhenNoTransferOperatorAvailable ?? true
        );

        // Clamp the current value to the limit
        const currentMaxConcurrent = campaign.maxConcurrentCalls || 1;
        const clampedValue = maxAllowed ? Math.min(currentMaxConcurrent, maxAllowed) : currentMaxConcurrent;

        const campaignNotificationEmails = campaign.notificationEmails || [];
        setConfigForm({
          name: campaign.name || '',
          description: campaign.description || '',
          notificationEmails: campaignNotificationEmails,
          automaticConversionEmailsEnabled:
            campaign.automaticConversionEmailsEnabled ?? true,
          maxConcurrentCalls: clampedValue,
          maxRetries: campaign.maxRetries || 3,
          retryDelayMinSeconds: campaign.retryDelayMinSeconds || 0,
          retryDelayMaxSeconds: campaign.retryDelayMaxSeconds || 0,
          schedule: normalizeScheduleEntriesForState(
            (((campaign as unknown as { schedule?: Array<Record<string, unknown>> }).schedule) || [])
          ),
        });
        setNotificationEmailsInput(campaignNotificationEmails.join(', '));
        setNotificationEmailsError(null);
      } catch (err) {
        console.error('Error loading campaign bootstrap:', err);
        setOperatingHours(DEFAULT_TENANT_OPERATING_HOURS);
        setConfigIvrMaxAudioSeconds(DEFAULT_IVR_MAX_AUDIO_SECONDS);
        setConfigIvrMaxAudioFileSizeBytes(null);
        setConfigReverseIvrAudioOptions([]);
        setConfigSelectedReverseIvrAudioId(campaign.ivrAudioFileId || '');
        setConfigSelectedReverseIvrAudioPreviewUrl(null);
        setConfigReverseIvrUseNewAudio(false);
        setConfigIvrDtmfRules(parseIvrDtmfRules(campaign.ivrDtmfMap));
        setProviderResourcesByProvider({});
        setAvailableAgents([]);
        setConfigSelectedAgentId(campaign.aiAgentId || '');
        setAvailableScripts([]);
        setConfigSelectedScriptIds((scripts || []).map((script) => script.id));
        setTransferTelephonyPackEnabled(false);
        setTransferUsers([]);
        setTransferGroups([]);
        setConfigTransferTargetType(
          (campaign.transferTargetType ?? '') as CampaignTransferTargetType | ''
        );
        setConfigTransferTargetId(campaign.transferTargetId ? String(campaign.transferTargetId) : '');
        setConfigSuspendWhenNoTransferOperatorAvailable(
          campaign.suspendWhenNoTransferOperatorAvailable ?? true
        );
        setResourcesError(t('pages:campaigns.detail.config.resourcesLoadFailed'));
        // Fallback to campaign value if API call fails
        const campaignNotificationEmails = campaign.notificationEmails || [];
        setConfigForm({
          name: campaign.name || '',
          description: campaign.description || '',
          notificationEmails: campaignNotificationEmails,
          automaticConversionEmailsEnabled:
            campaign.automaticConversionEmailsEnabled ?? true,
          maxConcurrentCalls: campaign.maxConcurrentCalls || 1,
          maxRetries: campaign.maxRetries || 3,
          retryDelayMinSeconds: campaign.retryDelayMinSeconds || 0,
          retryDelayMaxSeconds: campaign.retryDelayMaxSeconds || 0,
          schedule: normalizeScheduleEntriesForState(
            (((campaign as unknown as { schedule?: Array<Record<string, unknown>> }).schedule) || [])
          ),
        });
        setNotificationEmailsInput(campaignNotificationEmails.join(', '));
        setNotificationEmailsError(null);
      } finally {
        setResourcesLoading(false);
      }
      setConfigEditing(false);
    }
    setConfigModalOpen(true);
  };

  const handleSaveConfig = async () => {
    if (!campaign) return;
    const { error: emailsValidationError } = validateNotificationEmails(notificationEmailsInput);
    if (emailsValidationError) {
      setNotificationEmailsError(emailsValidationError);
      return;
    }

    setConfigSaving(true);
    try {
      // Convert empty strings to null for time fields
      const payload: Record<string, unknown> = {
        ...configForm,
      };

      const isReverseIvr = (campaign.campaignType ?? 'ai') === 'reverse_ivr';
      const isAi = (campaign.campaignType ?? 'ai') === 'ai';
      if (isAi && !configSelectedAgentId) {
        setResourcesError(t('pages:campaigns.create.errors.selectAgent'));
        return;
      }
      if (isReverseIvr) {
        if (configIvrDtmfValidationErrorKey) {
          setResourcesError(t(configIvrDtmfValidationErrorKey));
          return;
        }
        if (!hasConfigSelectedReverseIvrAudio) {
          setConfigIvrAudioError(t('pages:campaigns.create.errors.ivrAudioRequired'));
          return;
        }

        if (configReverseIvrUseNewAudio && configIvrAudioFile) {
          if (
            configIvrAudioDurationSeconds !== null &&
            configIvrAudioDurationSeconds > configIvrMaxAudioSeconds
          ) {
            setConfigIvrAudioError(
              t('pages:campaigns.create.errors.ivrAudioTooLong', {
                maxSeconds: configIvrMaxAudioSeconds,
              }),
            );
            return;
          }

          setConfigIvrAudioUploading(true);
          setConfigIvrAudioUploadProgress(0);

          const audioFormData = new FormData();
          audioFormData.append('file', configIvrAudioFile);
          audioFormData.append('file_type', 'reverse_ivr_audio');
          if (configIvrAudioDisplayName?.trim()) {
            audioFormData.append('display_name', configIvrAudioDisplayName.trim());
          }
          if (configIvrAudioDurationSeconds !== null) {
            audioFormData.append(
              'metadata',
              JSON.stringify({ duration_seconds: Number(configIvrAudioDurationSeconds.toFixed(3)) }),
            );
          }

          const audioUploadResponse = await api.post('/api/media/upload/', audioFormData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              if (!progressEvent.total) {
                setConfigIvrAudioUploadProgress(null);
                return;
              }
              const percentCompleted = (progressEvent.loaded * 100) / progressEvent.total;
              setConfigIvrAudioUploadProgress(percentCompleted);
            },
          });
          payload.ivrAudioFileId = audioUploadResponse.data.id;
        } else if (configSelectedReverseIvrAudioId) {
          payload.ivrAudioFileId = configSelectedReverseIvrAudioId;
        } else if (campaign.ivrAudioFileId) {
          payload.ivrAudioFileId = campaign.ivrAudioFileId;
        }
        payload.ivrDtmfMap = buildIvrDtmfMapPayload(configIvrDtmfRules);
      }

      if (isAi) {
        payload.aiAgentId = configSelectedAgentId;
      }
      if (isAi && (transferTelephonyPackEnabled || campaign.transferTargetType || campaign.transferTargetId)) {
        payload.transferTargetType = configTransferTargetType || null;
        payload.transferTargetId = configTransferTargetId || null;
        payload.suspendWhenNoTransferOperatorAvailable = configSuspendWhenNoTransferOperatorAvailable;
      }

      await api.patch(`/api/campaigns/${campaign.id}/`, payload);
      if (availableScripts.length > 0 || configSelectedScriptIds.length > 0) {
        await api.post(`/api/campaigns/${campaign.id}/scripts/`, {
          scriptIds: configSelectedScriptIds,
        });
        await fetchScripts();
        await fetchPreCallStatus();
      }
      await fetchCampaign();
      resetConfigIvrAudioDraft();
      setConfigEditing(false);
    } catch (err: unknown) {
      console.error('Failed to save configuration:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save configuration';
      alert(errorMessage);
    } finally {
      setConfigIvrAudioUploading(false);
      setConfigIvrAudioUploadProgress(null);
      setConfigSaving(false);
    }
  };

  const handleCloseConfigModal = () => {
    setConfigModalOpen(false);
    setConfigEditing(false);
    resetConfigIvrAudioDraft();
  };

  const toggleConfigScriptSelection = useCallback((scriptId: string) => {
    const selectedScript = availableScripts.find((script) => script.id === scriptId);
    setConfigSelectedScriptIds((prev) => {
      if (prev.includes(scriptId)) {
        return prev.filter((id) => id !== scriptId);
      }
      if (selectedScript?.eventType !== 'pre_call') {
        return [...prev, scriptId];
      }
      return [
        ...prev.filter((id) => availableScripts.find((script) => script.id === id)?.eventType !== 'pre_call'),
        scriptId,
      ];
    });
  }, [availableScripts]);

  useEffect(() => () => {
    if (configIvrAudioPreviewUrl) {
      URL.revokeObjectURL(configIvrAudioPreviewUrl);
    }
  }, [configIvrAudioPreviewUrl]);

  useEffect(() => {
    if (!configModalOpen) {
      return;
    }

    if ((campaign?.campaignType ?? 'ai') !== 'ai' || !campaign?.aiAgentProviderId) {
      setAvailableAgents([]);
      setConfigSelectedAgentId('');
      return;
    }

    const selectedProviderResources = providerResourcesByProvider[campaign.aiAgentProviderId];
    setAvailableAgents(selectedProviderResources?.agents ?? []);
    setConfigSelectedAgentId((current) => current || campaign.aiAgentId || '');
  }, [configModalOpen, campaign?.campaignType, campaign?.aiAgentId, campaign?.aiAgentProviderId, providerResourcesByProvider]);

  useEffect(() => {
    if (!configModalOpen) {
      setConfigSelectedAgentId('');
      setResourcesError(null);
    }
  }, [configModalOpen]);

  const campaignType = (campaign?.campaignType ?? 'ai') as 'ai' | 'reverse_ivr';
  const isReverseIvrCampaign = campaignType === 'reverse_ivr';
  const isAiCampaign = campaignType === 'ai';
  const hasPreCall = Boolean(preCallStatus?.enabled);
  const CampaignTypeIcon = isReverseIvrCampaign ? Dialpad : PsychologyIcon;
  const hasScriptsTab = availableScripts.length > 0 || scripts.length > 0;
  const selectedCampaignAgentRequiresTransfer = useMemo(() => {
    if (!isAiCampaign || !campaign?.aiAgentId) {
      return false;
    }
    const requiredTransferAgentIds = new Set(
      availableAgents
        .filter((agent) => Boolean(agent.transferTargetRequired))
        .map((agent) => String(agent.id)),
    );
    if (requiredTransferAgentIds.size === 0) {
      return false;
    }
    return requiredTransferAgentIds.has(String(campaign.aiAgentId));
  }, [availableAgents, campaign?.aiAgentId, isAiCampaign]);
  const hasTransferTab = isAiCampaign && selectedCampaignAgentRequiresTransfer;
  const showContactCostCurrencyColumn = canViewCostCurrency && !isReverseIvrCampaign;
  const showContactCreditsColumn = canViewCostCredits && !isReverseIvrCampaign;
  const showContactConsumptionColumn = !isReverseIvrCampaign;
  const showCallOriginColumn = !isReverseIvrCampaign;
  const showCallAgentColumn = !isReverseIvrCampaign;
  const showCallConsumptionColumn = !isReverseIvrCampaign;
  const showCallCostCurrencyColumn = canViewCostCurrency && !isReverseIvrCampaign;
  const showCallCreditsColumn = canViewCostCredits && !isReverseIvrCampaign;
  const contactsTableColumnCount =
    (showContactConsumptionColumn ? 6 : 5) +
    (hasPreCall ? 1 : 0) +
    (showContactCostCurrencyColumn ? 1 : 0) +
    (showContactCreditsColumn ? 1 : 0);
  const callsTableColumnCount =
    (showCallConsumptionColumn ? 5 : 4) +
    (showCallOriginColumn ? 1 : 0) +
    (showCallAgentColumn ? 1 : 0) +
    (showCallCostCurrencyColumn ? 1 : 0) +
    (showCallCreditsColumn ? 1 : 0);
  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={40} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
        <Skeleton variant="rectangular" height={400} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (!campaign) {
    return (
      <Box>
        <Typography color="error">{t('common:common.noData')}</Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/campaigns')} sx={{ mt: 2 }}>
          {t('pages:campaigns.detail.back')}
        </Button>
      </Box>
    );
  }

  // Use the API's progressPercentage which measures contacts no longer pending.
  // This includes terminal outcomes such as skipped after campaign cancellation.
  const progress = campaign.progressPercentage ?? 0;
  const { status, suspendedReason } = getCampaignDisplayStatusState(campaign);
  const isSuspended = status === 'suspended';
  const nextRunLabel = formatDateTime(campaign.nextRunAt, {
    locale: i18n.language,
    fallback: '-',
  });
  const suspendedReasonOptions = buildCampaignSuspendedReasonI18nOptions(campaign, {
    nextRun: nextRunLabel,
    transferTargetFallback: t(CAMPAIGN_TRANSFER_TARGET_FALLBACK_KEY),
  });
  const suspendedDescription = suspendedReason
    ? t(CAMPAIGN_SUSPENDED_REASON_TOOLTIP_KEYS[suspendedReason], suspendedReasonOptions)
    : undefined;

  const breadcrumbs = [
    { label: t('common:menu.campaigns'), to: '/campaigns' },
    { label: campaign.name },
  ];

  const headerActions = (
    <>
      <CampaignStatusChip status={status} size="small" description={suspendedDescription} />

      <Tooltip title={t('pages:campaigns.detail.refresh')}>
        <IconButton onClick={() => refreshAllData()} size="small">
          <Refresh />
        </IconButton>
      </Tooltip>

      <Tooltip title={t('pages:campaigns.detail.configuration')}>
        <IconButton onClick={handleOpenConfigModal} size="small">
          <Settings />
        </IconButton>
      </Tooltip>

      <Tooltip title={t('pages:campaigns.detail.analyticsCard.action')} arrow>
        <IconButton onClick={() => navigate(`/campaigns/${id}/analytics`)} size="small">
          <Insights />
        </IconButton>
      </Tooltip>

      {campaign.contactsFileUrl && (
        <Tooltip title={t('pages:campaigns.detail.viewContacts')}>
          <IconButton onClick={() => setContactsModalOpen(true)} size="small">
            <People />
          </IconButton>
        </Tooltip>
      )}

      {(campaign.status === 'completed' ||
        campaign.status === 'cancelled' ||
        campaign.status === 'paused') && (
          <RequirePermission permission="campaigns_create">
            <Tooltip title={t('pages:campaigns.detail.cloneCampaign')}>
              <IconButton onClick={handleCloneCampaign} size="small">
                <FileCopy />
              </IconButton>
            </Tooltip>
          </RequirePermission>
        )}

      {campaign.status !== 'completed' && (
        <RequirePermission permission="campaigns_control">
          <Tooltip title={t('pages:campaigns.detail.cancelCampaign')}>
            <IconButton onClick={handleCancelCampaign} size="small" color="error">
              <Cancel />
            </IconButton>
          </Tooltip>
        </RequirePermission>
      )}

      {campaign.status === 'active' && (
        <RequirePermission permission="campaigns_control">
          <Tooltip title={t('pages:campaigns.detail.pauseCampaign')}>
            <IconButton onClick={() => handleAction('pause')} size="small" color="warning">
              <Pause />
            </IconButton>
          </Tooltip>
        </RequirePermission>
      )}

      {(campaign.status === 'draft' ||
        campaign.status === 'ready' ||
        campaign.status === 'paused') &&
        !campaign.hasConfigError && (
          <RequirePermission permission="campaigns_control">
            <Tooltip
              title={
                campaign.status === 'paused'
                  ? t('pages:campaigns.detail.resumeCampaign')
                  : t('pages:campaigns.detail.startCampaign')
              }
            >
              <IconButton
                onClick={() => handleAction(campaign.status === 'paused' ? 'resume' : 'start')}
                size="small"
                color="success"
              >
                <PlayArrow />
              </IconButton>
            </Tooltip>
          </RequirePermission>
        )}
    </>
  );


  return (
    <Box>
      <DetailHeader
        title={campaign.name}
        sectionIcon={<CampaignIcon sx={{ fontSize: 32, color: 'primary.main' }} />}
        entityId={campaign.id}
        breadcrumbs={breadcrumbs}
        actions={headerActions}
        backHref="/campaigns"
        onBack={handleCampaignBack}
      />

      {isSuspended && suspendedReason && (
        <Alert severity="warning" sx={{ mb: 2, backgroundColor: "warning.light", color: "warning.contrastText", '& .MuiAlert-icon': { color: "warning.dark" } }}>
          <AlertTitle>{t(CAMPAIGN_DETAIL_SUSPENDED_REASON_TITLE_KEYS[suspendedReason], suspendedReasonOptions)}</AlertTitle>
          {t(CAMPAIGN_DETAIL_SUSPENDED_REASON_MESSAGE_KEYS[suspendedReason], suspendedReasonOptions)}
        </Alert>
      )}

      {/* Config Error Alert (Circuit Breaker) */}
      {campaign.hasConfigError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <RequirePermission permission="campaigns_control">
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => handleAction('clear-error')}
                >
                  {t('pages:campaigns.detail.configError.clearError')}
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  onClick={async () => {
                    await handleAction('clear-error');
                    await handleAction('resume');
                  }}
                >
                  {t('pages:campaigns.detail.configError.fixAndResume')}
                </Button>
              </Box>
            </RequirePermission>
          }
        >
          <AlertTitle>{t('pages:campaigns.detail.configError.title')}</AlertTitle>
          {campaign.pausedReason || t('pages:campaigns.detail.configError.message')}
        </Alert>
      )}

      {/* Stats Bar - Compact */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            {/* Campaign type */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CampaignTypeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" fontWeight={700}>{t(CAMPAIGN_TYPE_LABEL_KEYS[campaignType])}</Typography>
            </Box>

            <Divider orientation="vertical" flexItem />

            {/* Progress */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 150 }}>
              <Tooltip title={t('pages:campaigns.progressTooltip')} arrow>
                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
              <Box sx={{ flex: 1, minWidth: 80 }}>
                <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
              </Box>
              <Typography variant="body2" fontWeight={600}>{formatPercent(progress)}</Typography>
            </Box>

            <Divider orientation="vertical" flexItem />

            {/* Contacts */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <People sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2">
                <strong>{campaign.totalContacts}</strong>
              </Typography>
              <Tooltip title={t('pages:campaigns.contactsTooltip')} arrow>
                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>

            <Divider orientation="vertical" flexItem />

            {/* Calls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2">
                <strong style={{ color: '#4caf50' }}>{campaign.connectedCalls}</strong>
                <Typography component="span" variant="caption" color="text.secondary"> / </Typography>
                <strong>{campaign.totalCalls}</strong>
              </Typography>
              <Tooltip title={t('pages:campaigns.callsTooltip')} arrow>
                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>

            {hasBilledMinutes && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CampaignConsumptionDisplay
                      aiBilledSeconds={campaignAiBilledSeconds}
                      aiGrossBilledSeconds={campaignAiGrossBilledSeconds}
                      aiRefundedSeconds={campaignAiRefundedSeconds}
                      telephonyBilledSeconds={campaignHumanBilledSeconds}
                      telephonyGrossBilledSeconds={campaignHumanGrossBilledSeconds}
                      telephonyRefundedSeconds={campaignHumanRefundedSeconds}
                      aiDurationSeconds={campaignAiDurationSeconds}
                      telephonyDurationSeconds={campaignHumanDurationSeconds}
                      fallbackBilledSeconds={billedSeconds}
                      fallbackDurationSeconds={effectiveSeconds}
                      compact
                    />
                  </Box>
                  <Tooltip title={t('pages:campaigns.detail.results.callDurationTooltip')} arrow>
                    <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>
              </>
            )}

            {canViewCostCurrency && stats && ((stats.totalEstimatedCostUsd ?? 0) > 0 || stats.totalCost > 0) && (
              <>
                <Divider orientation="vertical" flexItem />
                <Tooltip title={
                  stats.totalEstimatedCostUsd
                    ? t('pages:campaigns.detail.tierBasedCost', { tier: formatCurrency(stats.totalEstimatedCostUsd, 'USD'), approx: formatCurrency(stats.totalCost, 'USD') })
                    : t('pages:campaigns.detail.approximateCost')
                }>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                    <Typography variant="body2">
                      <strong>{formatCurrency(stats.totalEstimatedCostUsd ?? stats.totalCost, 'USD')}</strong>
                    </Typography>
                    <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                  </Box>
                </Tooltip>
              </>
            )}

            {canViewCostCredits && stats && (stats.totalCostCredits ?? 0) > 0 && (
              <>
                <Divider orientation="vertical" flexItem />
                <Tooltip title={t('pages:campaigns.detail.totalCreditsConsumed', { count: stats.totalCostCredits ?? 0 })}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                    <Typography variant="body2">
                      <strong>{formatNumber(stats.totalCostCredits ?? 0)}</strong>
                    </Typography>
                    <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                  </Box>
                </Tooltip>
              </>
            )}

            {canViewCostCurrency && stats && (stats.costPerMinuteUsd ?? 0) > 0 && (
              <>
                <Divider orientation="vertical" flexItem />
                <Tooltip title={t('pages:campaigns.detail.costPerMinute')}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                    <Typography variant="body2">
                      <strong>{formatCurrency(stats.costPerMinuteUsd ?? 0, 'USD')}</strong>
                      <Typography component="span" variant="caption" color="text.secondary">/min</Typography>
                    </Typography>
                    <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                  </Box>
                </Tooltip>
              </>
            )}

            {stats && stats.avgDuration > 0 && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    <strong>{formatDuration(stats.avgDuration, true)}</strong>
                  </Typography>
                  <Tooltip title={t('pages:campaigns.avgDurationTooltip')} arrow>
                    <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>
              </>
            )}

            {stats && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Verified sx={{ fontSize: 16, color: 'success.main' }} />
                  <Typography variant="body2">
                    <strong style={{ color: '#00a884' }}>{formatPercent(stats.contactConversionRate ?? stats.conversionRate)}</strong>
                    <Tooltip title={t('pages:campaigns.contactConversionRateTooltip')} arrow>
                      <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help', ml: 0.5 }} />
                    </Tooltip>
                  </Typography>
                </Box>
              </>
            )}

            {stats && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <People sx={{ fontSize: 16, color: 'primary.main' }} />
                  <Typography variant="body2">
                    <strong style={{ color: '#1976d2' }}>{formatPercent(stats.attemptRate ?? 0)}</strong>
                    <Tooltip title={t('pages:campaigns.attemptRateTooltip')} arrow>
                      <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help', ml: 0.5 }} />
                    </Tooltip>
                  </Typography>
                </Box>
              </>
            )}

            {stats && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Phone sx={{ fontSize: 16, color: 'primary.main' }} />
                  <Typography variant="body2">
                    <strong style={{ color: '#4caf50' }}>{formatPercent(stats.contactAnswerRate ?? stats.answerRate ?? 0)}</strong>
                    <Tooltip title={t('pages:campaigns.contactAnswerRateTooltip')} arrow>
                      <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help', ml: 0.5 }} />
                    </Tooltip>
                  </Typography>
                </Box>
              </>
            )}

            {canViewCostCurrency && stats && stats.avgResponseLatency != null && (
              <>
                <Divider orientation="vertical" flexItem />
                <Tooltip title={t('pages:campaigns.detail.llmLatency', { llm: formatLatency((stats.avgLlmLatency ?? 0), true), tts: formatLatency((stats.avgTtsLatency ?? 0), true), max: formatLatency((stats.maxResponseLatency ?? 0), true) })}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                    <Typography variant="body2">
                      <strong style={{ color: stats.avgResponseLatency > 1.5 ? '#f44336' : stats.avgResponseLatency > 1.0 ? '#ff9800' : '#4caf50' }}>
                        {formatLatency(stats.avgResponseLatency, true)}
                      </strong>
                    </Typography>
                    <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                  </Box>
                </Tooltip>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Import Progress Indicator (from WebSocket) - Full Width */}
      {effectiveImportProgress && (
        <ImportProgressIndicator importProgress={effectiveImportProgress} fullWidth />
      )}

      {/* WebSocket Connection Status */}
      {!wsConnected && campaign?.status === 'processing' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('pages:campaigns.detail.wsUnavailable')}
        </Alert>
      )}

      <Card
        sx={{
          mb: 3,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.secondary.main, 0.06)} 100%)`,
        }}
      >
        <CardContent
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <Insights sx={{ color: 'primary.main', mt: 0.25 }} />
            <Box>
              <Typography variant="h6">
                {t('pages:campaigns.detail.analyticsCard.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('pages:campaigns.detail.analyticsCard.subtitle')}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            color="primary"
            endIcon={<OpenInNew />}
            onClick={() => navigate(`/campaigns/${id}/analytics`)}
          >
            {t('pages:campaigns.detail.analyticsCard.action')}
          </Button>
        </CardContent>
      </Card>

      {/* Campaign Progress */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              <Timeline sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('pages:campaigns.detail.results.progressTitle')}
            </Typography>
          </Box>

          {/* Tabs for Contacts and Calls with Search/Export aligned */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <ButtonTabs
                value={activeResultsTab}
                tabs={[
                  {
                    id: 'contacts',
                    label: t('pages:campaigns.detail.results.tabContacts'),
                    tabProps: {
                      icon: <People sx={{ fontSize: 18 }} />,
                      iconPosition: 'start',
                    },
                  },
                  {
                    id: 'calls',
                    label: t('pages:campaigns.detail.results.tabCalls'),
                    tabProps: {
                      icon: <Phone sx={{ fontSize: 18 }} />,
                      iconPosition: 'start',
                    },
                  },
                ]}
                defaultTab="contacts"
                onChange={handleResultsTabChange}
                tabsSx={{ flex: 1 }}
                renderContent={false}
              />

              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  flexWrap: { xs: 'wrap', lg: 'nowrap' },
                  ml: 2,
                  minWidth: 0,
                }}
              >
                <TextField
                  size="small"
                  placeholder={
                    isCallsTab
                      ? t('pages:campaigns.detail.results.searchCallsPlaceholder')
                      : t('pages:campaigns.detail.results.searchPlaceholder')
                  }
                  value={isCallsTab ? callsSearchInput : contactSearchInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (isCallsTab) {
                      setCallsSearchInput(value);
                    } else {
                      setContactSearchInput(value);
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: { xs: '100%', sm: 360, md: 440, lg: 520 },
                    maxWidth: '100%',
                  }}
                />

                {/* Export Button - show for both tabs */}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={isExporting ? <CircularProgress size={16} /> : <Download />}
                  onClick={handleOpenExportModal}
                  disabled={!campaign}
                >
                  {isExporting ? t('pages:campaigns.detail.results.generating') : t('pages:campaigns.detail.results.exportResults')}
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Tab 0: Contacts Table */}
          {activeResultsTab === 'contacts' && (
            <>
              {/* Contact Status Summary Cards (clickable filters) */}
              {stats && (() => {
                const contactStatusCards = [
                  {
                    label: t('pages:campaigns.detail.statusCards.pending'),
                    value: stats.pendingContacts,
                    color: '#9e9e9e',
                    filter: 'pending',
                    tooltip: t('pages:campaigns.detail.statusCards.pendingTooltip'),
                  },
                  {
                    label: t('pages:campaigns.detail.statusCards.inProgress'),
                    value: stats.inProgressContacts,
                    color: '#03a9f4',
                    filter: 'in_progress',
                    tooltip: t('pages:campaigns.detail.statusCards.inProgressTooltip'),
                  },
                  {
                    label: t('pages:campaigns.detail.statusCards.reached'),
                    value: stats.reachedContacts,
                    color: '#4caf50',
                    filter: 'reached',
                    tooltip: t('pages:campaigns.detail.statusCards.reachedTooltip'),
                  },
                  {
                    label: t('pages:campaigns.detail.statusCards.converted'),
                    value: stats.convertedContacts,
                    color: '#00a884',
                    filter: 'converted',
                    tooltip: t('pages:campaigns.detail.statusCards.convertedTooltip'),
                  },
                  {
                    label: t('pages:campaigns.detail.statusCards.optout'),
                    value: stats.optoutContacts,
                    color: '#9c27b0',
                    filter: 'optout',
                    tooltip: t('pages:campaigns.detail.statusCards.optoutTooltip'),
                  },
                  {
                    label: t('pages:campaigns.detail.statusCards.mismatch'),
                    value: stats.mismatchContacts,
                    color: '#ff9800',
                    filter: 'mismatch',
                    tooltip: t('pages:campaigns.detail.statusCards.mismatchTooltip'),
                  },
                  {
                    label: t('pages:campaigns.detail.statusCards.exhausted'),
                    value: stats.exhaustedContacts,
                    color: '#ff5c5c',
                    filter: 'exhausted',
                    tooltip: t('pages:campaigns.detail.statusCards.exhaustedTooltip'),
                  },
                  {
                    label: t('pages:campaigns.detail.statusCards.noValidPhones'),
                    value: stats.noValidPhonesContacts || 0,
                    color: '#e65100',
                    filter: 'no_valid_phones',
                    tooltip: t('pages:campaigns.detail.statusCards.noValidPhonesTooltip'),
                  },
                ].filter((item) => item.value > 0);
                const visibleContactStatusFilters = contactStatusCards.map((item) => item.filter);

                return (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    {contactStatusCards.map((item) => {
                      const isSelected = contactStatusFilters.length === 0 || contactStatusFilters.includes(item.filter);
                      const isExplicit = contactStatusFilters.includes(item.filter);
                      const baseBg = alpha(item.color, theme.palette.mode === 'dark' ? 0.18 : 0.08);
                      const explicitBg = alpha(item.color, theme.palette.mode === 'dark' ? 0.32 : 0.16);

                      return (
                        <Tooltip key={item.label} title={renderStatusCardTooltip(item.tooltip)} arrow placement="top">
                          <Paper
                            sx={{
                              position: 'relative',
                              p: 2,
                              textAlign: 'center',
                              cursor: 'pointer',
                              border: '1px solid',
                              borderColor: alpha(item.color, isSelected ? 0.75 : 0.4),
                              bgcolor: isExplicit ? explicitBg : baseBg,
                              transition: 'border-color 150ms ease, background-color 150ms ease, transform 150ms ease',
                              '&:hover': {
                                bgcolor: alpha(item.color, theme.palette.mode === 'dark' ? 0.36 : 0.2),
                                transform: 'translateY(-1px)',
                              },
                            }}
                            onClick={() =>
                              scheduleStatusCardInteraction(contactStatusClickTimeoutRef, () =>
                                toggleContactStatus(item.filter, visibleContactStatusFilters),
                              )
                            }
                            onDoubleClick={() =>
                              runStatusCardIsolation(contactStatusClickTimeoutRef, () =>
                                isolateContactStatus(item.filter, visibleContactStatusFilters),
                              )
                            }
                          >
                            <Checkbox
                              checked={isSelected}
                              disableRipple
                              tabIndex={-1}
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                p: 0.25,
                                pointerEvents: 'none',
                                color: alpha(item.color, 0.55),
                                '&.Mui-checked': { color: item.color },
                              }}
                            />
                            <Typography variant="h4" sx={{ color: item.color, fontWeight: 700 }}>
                              {item.value}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.label}
                            </Typography>
                          </Paper>
                        </Tooltip>
                      );
                    })}
                  </Box>
                );
              })()}

              <Box ref={resultsContainerRef} onScroll={handleScroll} sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {t('pages:campaigns.detail.results.contact')}
                            <Tooltip title={t('pages:campaigns.detail.results.contactTooltip')} arrow>
                              <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {t('pages:campaigns.detail.results.contactStatus')}
                            <Tooltip title={t('pages:campaigns.detail.results.contactStatusTooltip')} arrow>
                              <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                            </Tooltip>
                          </Box>
                        </TableCell>
                        {hasPreCall && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {t('pages:scripts.eventTypes.pre_call')}
                              <Tooltip title={t('pages:campaigns.detail.preCall.contactStateHelp')} arrow>
                                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </TableCell>
                        )}
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                            {t('pages:campaigns.detail.results.attempts')}
                            <Tooltip title={t('pages:campaigns.detail.results.attemptsTooltip')} arrow>
                              <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {t('pages:campaigns.detail.results.lastCall')}
                            <Tooltip title={t('pages:campaigns.detail.results.lastCallTooltip')} arrow>
                              <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                            </Tooltip>
                          </Box>
                        </TableCell>
                        {showContactConsumptionColumn && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {t('pages:calls.consumption')}
                              <Tooltip title={t('pages:campaigns.detail.results.callDurationTooltip')} arrow>
                                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </TableCell>
                        )}
                        {showContactCostCurrencyColumn && (
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                              {t('pages:campaigns.detail.results.cost')}
                              <Tooltip title={t('pages:campaigns.detail.results.costTooltip')} arrow>
                                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </TableCell>
                        )}
                        {showContactCreditsColumn && (
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                              {t('pages:campaigns.detail.results.credits')}
                              <Tooltip title={t('pages:campaigns.detail.results.creditsTooltip')} arrow>
                                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </TableCell>
                        )}
                        <TableCell align="center">{t('pages:campaigns.detail.results.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {contacts.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={contactsTableColumnCount}
                            align="center"
                            sx={{ py: 4 }}
                          >
                            <Typography color="text.secondary">
                              {contactSearchParam || contactStatusFilters.length > 0
                                ? t('pages:campaigns.detail.results.noContactsFiltered')
                                : t('pages:campaigns.detail.results.noContacts')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        contacts.map((contact) => {
                          const lastCall = contact.lastCall;
                          return (
                            <TableRow
                              key={contact.id}
                              hover
                              sx={{
                                bgcolor: contact.status === 'in_progress' ? 'action.selected' : undefined,
                              }}
                            >
                              <LinkTableCell to={`/campaigns/${id}/contacts/${contact.id}`}>
                                <Box>
                                  <Tooltip
                                    title={formatVariablesTooltip(contact.variables)}
                                    arrow
                                    placement="top"
                                    enterDelay={300}
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          bgcolor: 'rgba(0, 0, 0, 0.95)',
                                          opacity: 1,
                                          maxWidth: 600,
                                        },
                                      },
                                    }}
                                  >
                                    <Typography fontWeight={600} sx={{ cursor: 'help' }}>
                                      {formatDisplayName(contact.displayName)}
                                    </Typography>
                                  </Tooltip>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Phone fontSize="small" sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ fontFamily: 'monospace' }}
                                    >
                                      {formatPhone(contact.phone)}
                                    </Typography>
                                    {contact.phonesCount > 1 && (
                                      <Chip
                                        label={`+${contact.phonesCount - 1}`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: 18, fontSize: 10 }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                              </LinkTableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <ContactStatusChip status={contact.status} size="small" />
                                </Box>
                              </TableCell>
                              {hasPreCall && (
                                <TableCell>
                                  {contact.preCall ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                                      <Chip
                                        label={t(`pages:campaigns.detail.preCall.states.${contact.preCall.state}`)}
                                        size="small"
                                        variant="outlined"
                                        onClick={() => togglePreCallState(contact.preCall!.state)}
                                      />
                                      {contact.preCall.nextRetryAt && (
                                        <Typography variant="caption" color="text.secondary">
                                          {formatDateTime(contact.preCall.nextRetryAt, { locale, fallback: '-' })}
                                        </Typography>
                                      )}
                                    </Box>
                                  ) : (
                                    <Typography color="text.secondary">-</Typography>
                                  )}
                                </TableCell>
                              )}
                              <LinkTableCell to={`/campaigns/${id}/contacts/${contact.id}`} align="center">
                                <Typography>
                                  <strong>{contact.attemptCount}</strong>
                                  <Typography component="span" color="text.secondary">
                                    /{campaign?.maxRetries || 3}
                                  </Typography>
                                </Typography>
                              </LinkTableCell>
                              <TableCell>
                                {lastCall ? (
                                  <Box>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                      {formatPhone(lastCall.phone || contact.phone)}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <CallStatusChip
                                        status={lastCall.status as CallStatus}
                                        size="small"
                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                        isConverted={lastCall.isConverted}
                                        isOptout={lastCall.isOptout}
                                        isMismatch={lastCall.isMismatch}
                                      />
                                      {lastCall.duration > 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                          ({formatDuration(lastCall.duration)})
                                        </Typography>
                                      )}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                      {formatDateTime(lastCall.createdAt, { locale, fallback: '-' })}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                              {showContactConsumptionColumn && (
                                <LinkTableCell to={`/campaigns/${id}/contacts/${contact.id}`}>
                                  <ContactConsumptionDisplay
                                    aiBilledSeconds={contact.aiBilledSeconds ?? 0}
                                    aiGrossBilledSeconds={contact.aiGrossBilledSeconds ?? contact.aiBilledSeconds ?? 0}
                                    aiRefundedSeconds={contact.aiRefundedSeconds ?? 0}
                                    telephonyBilledSeconds={contact.telephonyBilledSeconds ?? 0}
                                    telephonyGrossBilledSeconds={contact.telephonyGrossBilledSeconds ?? contact.telephonyBilledSeconds ?? 0}
                                    telephonyRefundedSeconds={contact.telephonyRefundedSeconds ?? 0}
                                    aiDurationSeconds={contact.aiDurationSeconds ?? 0}
                                    telephonyDurationSeconds={contact.telephonyDurationSeconds ?? 0}
                                    billedSeconds={contact.totalBilledSeconds ?? contact.totalDuration ?? 0}
                                    effectiveSeconds={contact.totalDuration ?? 0}
                                  />
                                </LinkTableCell>
                              )}
                              {showContactCostCurrencyColumn && (
                                <LinkTableCell to={`/campaigns/${id}/contacts/${contact.id}`} align="right">
                                  <Typography variant="body2" color="text.secondary">
                                    {contact.totalCost > 0 ? formatCurrency(contact.totalCost, 'USD') : '-'}
                                  </Typography>
                                </LinkTableCell>
                              )}
                              {showContactCreditsColumn && (
                                <LinkTableCell to={`/campaigns/${id}/contacts/${contact.id}`} align="right">
                                  <Typography variant="body2" color="text.secondary">
                                    {contact.totalCostCredits > 0 ? formatNumber(contact.totalCostCredits) : '-'}
                                  </Typography>
                                </LinkTableCell>
                              )}
                              <LinkTableCell to={`/campaigns/${id}/contacts/${contact.id}`} align="center">
                                <Tooltip title={t('pages:campaigns.detail.results.viewDetails')}>
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                    <OpenInNew fontSize="small" />
                                  </Box>
                                </Tooltip>
                              </LinkTableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {contacts.length > 0 && (
                <LinkPagination
                  component="div"
                  count={totalContacts}
                  page={contactPage}
                  onPageChange={(_, newPage) => setContactPage(newPage)}
                  rowsPerPage={contactRowsPerPage}
                  onRowsPerPageChange={(e) => setContactRowsPerPage(Number(e.target.value))}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  labelRowsPerPage={t('common:pagination.rowsPerPage')}
                  labelDisplayedRows={({ from, to, count }) =>
                    count !== -1
                      ? `${from}-${to} ${t('common:common.of')} ${count}`
                      : `${from}-${to} ${t('common:common.of')} ${t('common:common.total')} >${to}`
                  }
                  getItemAriaLabel={(type) => {
                    if (type === 'first') return t('common:pagination.firstPage');
                    if (type === 'last') return t('common:pagination.lastPage');
                    if (type === 'next') return t('common:pagination.nextPage');
                    return t('common:pagination.previousPage');
                  }}
                  getPageLink={getContactsPageLink}
                />
              )}
            </>
          )}

          {/* Tab 1: Calls Table */}
          {activeResultsTab === 'calls' && (
            <>
              {/* Call Status Summary Cards (clickable filters) */}
              {stats && (() => {
                const queuedFilters = ['pending', 'queued'];
                const dialingFilters = ['dialing', 'analyzing'];
                const awaitingAnswerFilters = ['ringing'];
                const callStatusCards = [
                  {
                    label: t('pages:campaigns.detail.callStatusCards.queuedGroup'),
                    value: (stats.pendingCalls || 0) + (stats.queuedCalls || 0),
                    color: '#78909c',
                    filters: queuedFilters,
                    tooltip: t('pages:campaigns.detail.callStatusCards.queuedGroupTooltip'),
                  },
                  {
                    label: t('pages:campaigns.detail.callStatusCards.dialing'),
                    value: stats.dialingCalls || 0,
                    color: '#29b6f6',
                    filters: dialingFilters,
                    tooltip: t('pages:campaigns.detail.callStatusCards.dialingTooltip'),
                  },
                  {
                    label: t('pages:campaigns.detail.callStatusCards.awaitingAnswer'),
                    value: stats.ringingCalls || 0,
                    color: '#ffb300',
                    filters: awaitingAnswerFilters,
                    tooltip: t('pages:campaigns.detail.callStatusCards.awaitingAnswerTooltip'),
                  },
                  {
                    label: t('pages:campaigns.detail.callStatusCards.inProgress'),
                    value: stats.inProgressCalls,
                    color: '#03a9f4',
                    filters: ['in_progress'],
                    tooltip: t('pages:campaigns.detail.callStatusCards.inProgressTooltip'),
                  },
                  { label: t('pages:campaigns.detail.callStatusCards.completed'), value: stats.completedStatusCalls, color: '#4caf50', filters: ['completed'], tooltip: t('pages:campaigns.detail.callStatusCards.completedTooltip') },
                  { label: t('pages:campaigns.detail.callStatusCards.noInteraction'), value: stats.noInteraction ?? 0, color: '#ffa726', filters: ['no_interaction'], tooltip: t('pages:campaigns.detail.callStatusCards.noInteractionTooltip') },
                  { label: t('pages:campaigns.detail.callStatusCards.failed'), value: stats.failedOnlyCalls ?? 0, color: '#f44336', filters: ['failed'], tooltip: t('pages:campaigns.detail.callStatusCards.failedTooltip') },
                  { label: t('pages:campaigns.detail.callStatusCards.noAnswer'), value: stats.noAnswer || 0, color: '#ff9800', filters: ['no_answer'], tooltip: t('pages:campaigns.detail.callStatusCards.noAnswerTooltip') },
                  { label: t('pages:campaigns.detail.callStatusCards.busy'), value: stats.busy || 0, color: '#ff5722', filters: ['busy'], tooltip: t('pages:campaigns.detail.callStatusCards.busyTooltip') },
                  { label: t('pages:campaigns.detail.callStatusCards.cancelled'), value: stats.cancelled || 0, color: '#795548', filters: ['cancelled'], tooltip: t('pages:campaigns.detail.callStatusCards.cancelledTooltip') },
                  { label: t('pages:campaigns.detail.callStatusCards.voicemail'), value: stats.voicemail || 0, color: '#9c27b0', filters: ['voicemail'], tooltip: t('pages:campaigns.detail.callStatusCards.voicemailTooltip') },
                  { label: t('pages:campaigns.detail.callStatusCards.invalidNumber'), value: stats.invalidNumber || 0, color: '#e65100', filters: ['invalid_number'], tooltip: t('pages:campaigns.detail.callStatusCards.invalidNumberTooltip') },
                  { label: t('pages:campaigns.detail.callStatusCards.configError'), value: stats.configError || 0, color: '#d32f2f', filters: ['config_error'], tooltip: t('pages:campaigns.detail.callStatusCards.configErrorTooltip') },
                  { label: t('pages:campaigns.detail.callStatusCards.declined'), value: stats.declined || 0, color: '#7b1fa2', filters: ['declined'], tooltip: t('pages:campaigns.detail.callStatusCards.declinedTooltip') },
                  { label: t('pages:campaigns.detail.callStatusCards.unreachable'), value: stats.unreachable || 0, color: '#c62828', filters: ['unreachable'], tooltip: t('pages:campaigns.detail.callStatusCards.unreachableTooltip') },
                  { label: t('pages:campaigns.detail.callStatusCards.timeout'), value: stats.timeout || 0, color: '#ef6c00', filters: ['timeout'], tooltip: t('pages:campaigns.detail.callStatusCards.timeoutTooltip') },
                  { label: t('pages:campaigns.detail.callStatusCards.mismatch'), value: stats.mismatch || 0, color: '#ff9800', filters: ['mismatch'], tooltip: t('pages:campaigns.detail.callStatusCards.mismatchTooltip') },
                  { label: t('pages:campaigns.detail.callStatusCards.apiTimeout'), value: stats.apiTimeout || 0, color: '#f57c00', filters: ['api_timeout'], tooltip: t('pages:campaigns.detail.callStatusCards.apiTimeoutTooltip') },
                ].filter((item) => item.value > 0);
                const visibleCallStatusFilters = callStatusCards.flatMap((item) => item.filters);

                return (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    {callStatusCards.map((item) => {
                      const isSelected = callsStatusFilters.length === 0
                        || item.filters.every((value) => callsStatusFilters.includes(value));
                      const isExplicit = item.filters.some((value) => callsStatusFilters.includes(value));
                      const baseBg = alpha(item.color, theme.palette.mode === 'dark' ? 0.18 : 0.08);
                      const explicitBg = alpha(item.color, theme.palette.mode === 'dark' ? 0.32 : 0.16);

                      return (
                        <Tooltip key={item.label} title={renderStatusCardTooltip(item.tooltip)} arrow placement="top">
                          <Paper
                            sx={{
                              position: 'relative',
                              p: 2,
                              textAlign: 'center',
                              cursor: 'pointer',
                              border: '1px solid',
                              borderColor: alpha(item.color, isSelected ? 0.75 : 0.4),
                              bgcolor: isExplicit ? explicitBg : baseBg,
                              transition: 'border-color 150ms ease, background-color 150ms ease, transform 150ms ease',
                              '&:hover': {
                                bgcolor: alpha(item.color, theme.palette.mode === 'dark' ? 0.36 : 0.2),
                                transform: 'translateY(-1px)',
                              },
                            }}
                            onClick={() =>
                              scheduleStatusCardInteraction(callsStatusClickTimeoutRef, () =>
                                toggleCallsStatus(item.filters, visibleCallStatusFilters),
                              )
                            }
                            onDoubleClick={() =>
                              runStatusCardIsolation(callsStatusClickTimeoutRef, () =>
                                isolateCallsStatus(item.filters, visibleCallStatusFilters),
                              )
                            }
                          >
                            <Checkbox
                              checked={isSelected}
                              disableRipple
                              tabIndex={-1}
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                p: 0.25,
                                pointerEvents: 'none',
                                color: alpha(item.color, 0.55),
                                '&.Mui-checked': { color: item.color },
                              }}
                            />
                            <Typography variant="h4" sx={{ color: item.color, fontWeight: 700 }}>
                              {item.value}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.label}
                            </Typography>
                          </Paper>
                        </Tooltip>
                      );
                    })}
                  </Box>
                );
              })()}

              <Box ref={resultsContainerRef} onScroll={handleScroll} sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {showCallOriginColumn && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {t('pages:campaigns.detail.results.callPhone')}
                              <Tooltip title={t('pages:campaigns.detail.results.callPhoneTooltip')} arrow>
                                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </TableCell>
                        )}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {t('pages:campaigns.detail.results.callContact')}
                            <Tooltip title={t('pages:campaigns.detail.results.callContactTooltip')} arrow>
                              <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {t('pages:campaigns.detail.results.callStatus')}
                            <Tooltip title={t('pages:campaigns.detail.results.callStatusTooltip')} arrow>
                              <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                            </Tooltip>
                          </Box>
                        </TableCell>
                        {showCallAgentColumn && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {t('pages:campaigns.detail.results.callAgent')}
                              <Tooltip title={t('pages:campaigns.detail.results.callAgentTooltip')} arrow>
                                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </TableCell>
                        )}
                        {showCallConsumptionColumn && (
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                              {t('pages:calls.consumption')}
                              <Tooltip title={t('pages:campaigns.detail.results.callDurationTooltip')} arrow>
                                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </TableCell>
                        )}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {t('pages:campaigns.detail.results.callCreatedAt')}
                            <Tooltip title={t('pages:campaigns.detail.results.callCreatedAtTooltip')} arrow>
                              <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                            </Tooltip>
                          </Box>
                        </TableCell>
                        {showCallCostCurrencyColumn && (
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                              {t('pages:campaigns.detail.results.cost')}
                              <Tooltip title={t('pages:campaigns.detail.results.costTooltip')} arrow>
                                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </TableCell>
                        )}
                        {showCallCreditsColumn && (
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                              {t('pages:campaigns.detail.results.credits')}
                              <Tooltip title={t('pages:campaigns.detail.results.creditsTooltip')} arrow>
                                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          </TableCell>
                        )}
                        <TableCell align="center">{t('pages:campaigns.detail.results.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {callsLoading ? (
                        [...Array(5)].map((_, i) => (
                          <TableRow key={i}>
                            {[...Array(callsTableColumnCount)].map((_, j) => (
                              <TableCell key={j}><Skeleton /></TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : calls.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={callsTableColumnCount}
                            align="center"
                            sx={{ py: 4 }}
                          >
                            <Typography color="text.secondary">
                              {callsSearchParam || callsStatusFilters.length > 0
                                ? t('pages:campaigns.detail.results.noCallsFiltered')
                                : t('pages:campaigns.detail.results.noCalls')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        calls.map((call) => {
                          const returnTabParam = 'calls';
                          const contactLink = call.campaignContactId
                            ? `/campaigns/${id}/contacts/${call.campaignContactId}?call=${call.id}&returnTab=${returnTabParam}`
                            : '';
                          const hasContact = Boolean(call.campaignContactId);

                          return (
                            <TableRow key={call.id} hover>
                              {showCallOriginColumn && (
                                hasContact ? (
                                  <LinkTableCell to={contactLink}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                      {call.originLineName || '-'}
                                    </Typography>
                                  </LinkTableCell>
                                ) : (
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                      {call.originLineName || '-'}
                                    </Typography>
                                  </TableCell>
                                )
                              )}
                              {hasContact ? (
                                <LinkTableCell to={contactLink}>
                                  <Tooltip
                                    title={formatVariablesTooltip(call.campaignContactVariables)}
                                    arrow
                                    placement="top"
                                    enterDelay={300}
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          bgcolor: 'rgba(0, 0, 0, 0.95)',
                                          opacity: 1,
                                          maxWidth: 600,
                                        },
                                      },
                                    }}
                                  >
                                    <Typography sx={{ '&:hover': { textDecoration: 'underline' } }}>
                                      {call.campaignContactDisplayName || t('pages:campaigns.detail.results.contact')}
                                    </Typography>
                                  </Tooltip>
                                  <Box sx={{ mt: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Phone fontSize="small" sx={{ fontSize: 14, color: 'text.secondary' }} />
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontFamily: 'monospace' }}
                                      >
                                        {call.toNumber ? formatPhone(call.toNumber) : '-'}
                                      </Typography>
                                    </Box>
                                    {call.phoneLabel && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', ml: 2.4 }}
                                      >
                                        {call.phoneLabel}
                                      </Typography>
                                    )}
                                  </Box>
                                </LinkTableCell>
                              ) : (
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">-</Typography>
                                </TableCell>
                              )}
                              <TableCell>
                                <CallStatusChip status={call.status as CallStatus} isConverted={call.isConverted} isOptout={call.isOptout} isMismatch={call.isMismatch} size="small" />
                              </TableCell>
                              {showCallAgentColumn && (
                                hasContact ? (
                                  <LinkTableCell to={contactLink}>
                                    <Typography variant="body2" color="text.secondary">
                                      {call.agentName ?? '-'}
                                    </Typography>
                                  </LinkTableCell>
                                ) : (
                                  <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                      {call.agentName ?? '-'}
                                    </Typography>
                                  </TableCell>
                                )
                              )}
                              {showCallConsumptionColumn && (
                                hasContact ? (
                                  <LinkTableCell to={contactLink} align="right">
                                    <CallConsumptionDisplay
                                      segments={call.consumptionSegments}
                                      activeSegmentId={call.activeSegmentId}
                                      activeSegmentType={call.activeSegmentType}
                                      callStatus={call.status}
                                      fallbackBilledSeconds={
                                        call.segmentTotals?.totalBilledSeconds ?? call.billedSeconds ?? call.duration ?? 0
                                      }
                                      fallbackDurationSeconds={
                                        call.segmentTotals?.totalDurationSeconds ?? call.duration ?? 0
                                      }
                                      fallbackBucketType={isReverseIvrCampaign ? 'telephony' : 'ai'}
                                      compact
                                    />
                                  </LinkTableCell>
                                ) : (
                                  <TableCell align="right">
                                    <CallConsumptionDisplay
                                      segments={call.consumptionSegments}
                                      activeSegmentId={call.activeSegmentId}
                                      activeSegmentType={call.activeSegmentType}
                                      callStatus={call.status}
                                      fallbackBilledSeconds={
                                        call.segmentTotals?.totalBilledSeconds ?? call.billedSeconds ?? call.duration ?? 0
                                      }
                                      fallbackDurationSeconds={
                                        call.segmentTotals?.totalDurationSeconds ?? call.duration ?? 0
                                      }
                                      fallbackBucketType={isReverseIvrCampaign ? 'telephony' : 'ai'}
                                      compact
                                    />
                                  </TableCell>
                                )
                              )}
                              {hasContact ? (
                                <LinkTableCell to={contactLink}>
                                  <Typography variant="body2" color="text.secondary">
                                    {call.createdAt
                                      ? formatDateTime(call.createdAt, {
                                        locale,
                                        options: {
                                          day: '2-digit',
                                          month: '2-digit',
                                          year: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit',
                                          second: '2-digit',
                                        },
                                      })
                                      : '-'}
                                  </Typography>
                                </LinkTableCell>
                              ) : (
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {call.createdAt
                                      ? formatDateTime(call.createdAt, {
                                        locale,
                                        options: {
                                          day: '2-digit',
                                          month: '2-digit',
                                          year: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit',
                                          second: '2-digit',
                                        },
                                      })
                                      : '-'}
                                  </Typography>
                                </TableCell>
                              )}
                              {showCallCostCurrencyColumn && (
                                hasContact ? (
                                  <LinkTableCell to={contactLink} align="right">
                                    <Typography variant="body2" color="text.secondary">
                                      {call.estimatedCostUsd ? formatCurrency(call.estimatedCostUsd, 'USD') : '-'}
                                    </Typography>
                                  </LinkTableCell>
                                ) : (
                                  <TableCell align="right">
                                    <Typography variant="body2" color="text.secondary">
                                      {call.estimatedCostUsd ? formatCurrency(call.estimatedCostUsd, 'USD') : '-'}
                                    </Typography>
                                  </TableCell>
                                )
                              )}
                              {showCallCreditsColumn && (
                                hasContact ? (
                                  <LinkTableCell to={contactLink} align="right">
                                    <Typography variant="body2" color="text.secondary">
                                      {call.costCredits ? formatNumber(call.costCredits) : '-'}
                                    </Typography>
                                  </LinkTableCell>
                                ) : (
                                  <TableCell align="right">
                                    <Typography variant="body2" color="text.secondary">
                                      {call.costCredits ? formatNumber(call.costCredits) : '-'}
                                    </Typography>
                                  </TableCell>
                                )
                              )}
                              {hasContact ? (
                                <LinkTableCell to={contactLink} align="center">
                                  <Tooltip title={t('pages:campaigns.detail.results.viewDetails')}>
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                      <OpenInNew fontSize="small" />
                                    </Box>
                                  </Tooltip>
                                </LinkTableCell>
                              ) : (
                                <TableCell align="center">
                                  <Typography variant="body2" color="text.secondary">-</Typography>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {calls.length > 0 && (
                <LinkPagination
                  component="div"
                  count={totalCalls}
                  page={callsPage}
                  onPageChange={(_, newPage) => setCallsPage(newPage)}
                  rowsPerPage={callsRowsPerPage}
                  onRowsPerPageChange={(e) => setCallsRowsPerPage(Number(e.target.value))}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  labelRowsPerPage={t('common:pagination.rowsPerPage')}
                  labelDisplayedRows={({ from, to, count }) =>
                    count !== -1
                      ? `${from}-${to} ${t('common:common.of')} ${count}`
                      : `${from}-${to} ${t('common:common.of')} ${t('common:common.total')} >${to}`
                  }
                  getItemAriaLabel={(type) => {
                    if (type === 'first') return t('common:pagination.firstPage');
                    if (type === 'last') return t('common:pagination.lastPage');
                    if (type === 'next') return t('common:pagination.nextPage');
                    return t('common:pagination.previousPage');
                  }}
                  getPageLink={getCallsPageLink}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Contacts Viewer Modal */}
      {campaign && (
        <ContactsViewerModal
          open={contactsModalOpen}
          onClose={() => setContactsModalOpen(false)}
          campaignName={campaign.name}
          contactsFileUrl={campaign.contactsFileUrl || null}
          contactsFileId={campaign.contactsFileId}
          contactsFileOriginalName={campaign.contactsFileOriginalName}
          processedContactsFileUrl={campaign.processedContactsFileUrl || null}
          processedContactsFileId={campaign.processedContactsFileId}
          fileEncoding={campaign.fileEncoding}
          fileDelimiter={campaign.fileDelimiter}
          fileTextDelimiter={campaign.fileTextDelimiter}
          importStats={campaign.importStats}
          importProgress={effectiveImportProgress}
        />
      )}

      {/* Configuration Modal */}
      <Dialog
        open={configModalOpen}
        onClose={handleCloseConfigModal}
        maxWidth={false}
        fullWidth
        fullScreen={isSmallScreen}
        PaperProps={{
          sx: {
            width: isSmallScreen ? '100%' : '80vw',
            maxWidth: isSmallScreen ? '100%' : '80vw',
            height: isSmallScreen ? '100%' : 'min(720px, 75vh)',
            maxHeight: isSmallScreen ? '100%' : '85vh',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Settings />
              {t('pages:campaigns.detail.config.title')}
            </Box>
            {canEditCampaign && !configEditing && (
              <Button size="small" onClick={() => setConfigEditing(true)}>
                {t('pages:campaigns.detail.config.edit')}
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            pb: 3,
          }}
        >
          {campaign && (
            <ButtonTabs
              scrollable
              tabs={[
                {
                  id: 'basic-info',
                  label: t('pages:campaigns.detail.config.tabs.basicInfo'),
                  icon: <Description sx={{ fontSize: 18 }} />,
                  content: (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {configEditing ? (
                        <>
                          <TextField
                            label={t('pages:campaigns.detail.config.campaignName')}
                            value={configForm.name}
                            onChange={(e) => setConfigForm({ ...configForm, name: e.target.value })}
                            fullWidth
                            size="small"
                          />
                          <TextField
                            label={t('pages:campaigns.detail.config.description')}
                            value={configForm.description}
                            onChange={(e) => setConfigForm({ ...configForm, description: e.target.value })}
                            fullWidth
                            size="small"
                            multiline
                            rows={2}
                          />
                          {isReverseIvrCampaign && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Typography variant="subtitle2">
                                {t('pages:campaigns.create.basicInfo.ivrAudio')} *
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t('pages:campaigns.create.basicInfo.ivrAudioHelp', { maxSeconds: configIvrMaxAudioSeconds })}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <Autocomplete
                                  fullWidth
                                  options={configReverseIvrAudioOptions}
                                  value={selectedConfigReverseIvrAudioOption}
                                  disabled={configReverseIvrUseNewAudio}
                                  onChange={(_, option) => {
                                    setConfigIvrAudioError(null);
                                    setConfigSelectedReverseIvrAudioId(option?.id ?? '');
                                    setConfigSelectedReverseIvrAudioPreviewUrl(null);
                                  }}
                                  getOptionLabel={(option) => option.name || option.id}
                                  isOptionEqualToValue={(option, value) => option.id === value.id}
                                  noOptionsText={t('pages:campaigns.create.basicInfo.ivrAudioNoExisting', 'Nenhum áudio disponível')}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label={t('pages:campaigns.create.basicInfo.ivrAudioExisting', 'Áudio existente')}
                                      placeholder={t('pages:campaigns.create.basicInfo.ivrAudioExistingPlaceholder', 'Selecionar áudio')}
                                      size="small"
                                    />
                                  )}
                                  sx={{ flex: '1 1 420px', minWidth: 260 }}
                                />
                                <FormControlLabel
                                  control={(
                                    <Switch
                                      checked={configReverseIvrUseNewAudio}
                                      onChange={(_, checked) => {
                                        setConfigReverseIvrUseNewAudio(checked);
                                        if (!checked && configReverseIvrAudioOptions.length === 1) {
                                          setConfigSelectedReverseIvrAudioId(configReverseIvrAudioOptions[0].id);
                                        }
                                      }}
                                    />
                                  )}
                                  label={t('pages:campaigns.create.basicInfo.ivrAudioNewToggle', 'Novo')}
                                  sx={{ m: 0 }}
                                />
                              </Box>
                              {!configReverseIvrUseNewAudio && configSelectedReverseIvrAudioId && configReverseIvrAudioPreviewLoading && (
                                <Typography variant="caption" color="text.secondary">
                                  {t('pages:campaigns.create.basicInfo.ivrAudioPreviewLoading', 'Carregando preview do áudio...')}
                                </Typography>
                              )}
                              {!configReverseIvrUseNewAudio && selectedConfigReverseIvrAudioPreview && (
                                <WaveformPlayer
                                  url={selectedConfigReverseIvrAudioPreview}
                                  displayName={selectedConfigReverseIvrAudioOption
                                    ? getReverseIvrAudioDisplayName(selectedConfigReverseIvrAudioOption)
                                    : (campaign.ivrAudioFileId || undefined)}
                                  downloadFilename={getReverseIvrAudioDownloadFilename(selectedConfigReverseIvrAudioOption)}
                                  durationSeconds={selectedConfigReverseIvrAudioOption?.durationSeconds ?? null}
                                  displayNameEditable
                                  displayNameSaving={configIvrAudioFilenameSaving}
                                  onDisplayNameCommit={handleConfigIvrAudioDisplayNameCommit}
                                />
                              )}
                              {!configReverseIvrUseNewAudio
                                && configSelectedReverseIvrAudioId
                                && !configReverseIvrAudioPreviewLoading
                                && !selectedConfigReverseIvrAudioPreview && (
                                <Alert severity="info">
                                  {t(
                                    'pages:campaigns.create.basicInfo.ivrAudioPreviewUnavailable',
                                    'Não foi possível carregar o preview do áudio selecionado.',
                                  )}
                                </Alert>
                              )}
                              {configReverseIvrUseNewAudio && (
                                <WaveformPlayerField
                                  url={configIvrAudioPreviewUrl}
                                  displayName={configIvrAudioDisplayName || configIvrAudioFile?.name}
                                  downloadFilename={configIvrAudioFile?.name || undefined}
                                  durationSeconds={configIvrAudioDurationSeconds}
                                  displayNameEditable
                                  uploadEnabled
                                  required
                                  isUploading={configIvrAudioUploading}
                                  uploadProgress={configIvrAudioUploadProgress}
                                  displayNameSaving={configIvrAudioFilenameSaving}
                                  onDisplayNameCommit={handleConfigIvrAudioDisplayNameCommit}
                                  onSelectFile={handleConfigIvrAudioSelect}
                                  constraints={{
                                    maxDurationSeconds: configIvrMaxAudioSeconds,
                                    maxFileSizeBytes: configIvrMaxAudioFileSizeBytes || undefined,
                                  }}
                                  showConstraints
                                />
                              )}
                              {configIvrAudioError && (
                                <Alert severity="error">{configIvrAudioError}</Alert>
                              )}
                              <ReverseIvrDtmfEditor
                                rules={configIvrDtmfRules}
                                onChange={setConfigIvrDtmfRules}
                                required
                              />
                            </Box>
                          )}
                        </>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {t('pages:campaigns.detail.config.campaignName')}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {configForm.name || campaign.name}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {t('pages:campaigns.detail.config.description')}
                            </Typography>
                            <Typography variant="body2">
                              {configForm.description || campaign.description || '—'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {t('pages:campaigns.create.basicInfo.campaignType')}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {isReverseIvrCampaign
                                ? t('pages:campaigns.create.basicInfo.campaignTypeReverseIvr')
                                : t('pages:campaigns.create.basicInfo.campaignTypeAi')}
                            </Typography>
                          </Box>
                          {isReverseIvrCampaign && (
                            <>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {t('pages:campaigns.create.basicInfo.sipTrunkPool')}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {campaign.sipTrunkPoolName || '—'}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {t('pages:campaigns.create.basicInfo.ivrAudio')}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {readonlyReverseIvrAudioName}
                                </Typography>
                                {(readonlyReverseIvrAudioDuration !== null && readonlyReverseIvrAudioDuration !== undefined) || readonlyReverseIvrAudioSizeMb ? (
                                  <Typography variant="caption" color="text.secondary">
                                    {[
                                      readonlyReverseIvrAudioDuration !== null && readonlyReverseIvrAudioDuration !== undefined
                                        ? t('pages:campaigns.detail.config.ivrAudioDuration', {
                                          duration: formatDecimal(readonlyReverseIvrAudioDuration, 2),
                                        })
                                        : null,
                                      readonlyReverseIvrAudioSizeMb
                                        ? t('pages:campaigns.detail.config.ivrAudioSize', {
                                          size: readonlyReverseIvrAudioSizeMb,
                                        })
                                        : null,
                                    ].filter(Boolean).join(' • ')}
                                  </Typography>
                                ) : null}
                                {campaign.ivrAudioFileUrl ? (
                                  <Box sx={{ mt: 1 }}>
                                    <WaveformPlayer
                                      url={campaign.ivrAudioFileUrl}
                                      displayName={readonlyReverseIvrAudioName}
                                      downloadFilename={getReverseIvrAudioDownloadFilename(readonlyReverseIvrAudioOption)}
                                      durationSeconds={readonlyReverseIvrAudioDuration ?? null}
                                    />
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    {campaign.ivrAudioFileId ? t('common:common.processing') : '—'}
                                  </Typography>
                                )}
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {t('pages:campaigns.create.basicInfo.ivrDtmfMap')}
                                </Typography>
                                {Object.keys(campaign.ivrDtmfMap || {}).length > 0 ? (
                                  <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {Object.entries(campaign.ivrDtmfMap || {})
                                      .sort(([digitA], [digitB]) => digitA.localeCompare(digitB))
                                      .map(([digit, mapping]) => (
                                        <Typography key={digit} variant="body2">
                                          {digit} {'->'} {t(`pages:campaigns.create.basicInfo.ivrDtmfStatusValues.${mapping.status}`)}
                                          {mapping.label ? ` (${mapping.label})` : ''}
                                        </Typography>
                                      ))}
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    —
                                  </Typography>
                                )}
                              </Box>
                            </>
                          )}
                        </Box>
                      )}
                    </Box>
                  ),
                },
                {
                  id: 'notifications',
                  label: t('pages:campaigns.detail.config.tabs.notifications'),
                  icon: <Email sx={{ fontSize: 18 }} />,
                  content: (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <CampaignConversionEmailControl
                        checked={configForm.automaticConversionEmailsEnabled}
                        onChange={configEditing ? (checked) => {
                          setConfigForm((previous) => ({
                            ...previous,
                            automaticConversionEmailsEnabled: checked,
                          }));
                        } : undefined}
                        translationPrefix="pages:campaigns.detail.config"
                      />
                      {configEditing ? (
                        <TextField
                          label={
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography component="span">
                                {t('pages:campaigns.detail.config.notificationEmails')}
                              </Typography>
                              <Tooltip
                                title={t('pages:campaigns.detail.config.notificationEmailsTooltip')}
                                arrow
                              >
                                <HelpOutline
                                  sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }}
                                />
                              </Tooltip>
                            </Box>
                          }
                          value={notificationEmailsInput}
                          onChange={(event) => handleNotificationEmailsChange(event.target.value)}
                          onBlur={(event) => handleNotificationEmailsChange(event.target.value)}
                          placeholder={t('pages:campaigns.detail.config.notificationEmailsPlaceholder')}
                          helperText={
                            notificationEmailsError ||
                            t('pages:campaigns.detail.config.notificationEmailsHelper')
                          }
                          error={Boolean(notificationEmailsError)}
                          multiline
                          minRows={2}
                          fullWidth
                          size="small"
                        />
                      ) : (
                        <Box>
                          {configForm.notificationEmails.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {configForm.notificationEmails.map((email) => (
                                <Chip key={email} label={email} size="small" variant="outlined" />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {t('pages:campaigns.detail.config.notificationEmailsEmpty')}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  ),
                },
                {
                  id: 'concurrency',
                  label: t('pages:campaigns.detail.config.tabs.concurrency'),
                  icon: <Hub sx={{ fontSize: 18 }} />,
                  content: configEditing ? (
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        {t('pages:campaigns.detail.config.maxConcurrentCalls')}
                        {hasFiniteMaxConcurrent && ` (${t('pages:campaigns.create.settings.maxConcurrentCallsLimit', { limit: maxAllowedConcurrent })})`}
                      </Typography>
                      {hasFiniteMaxConcurrent ? (
                        <Slider
                          value={configForm.maxConcurrentCalls}
                          onChange={(_, newValue) => {
                            setConfigForm({ ...configForm, maxConcurrentCalls: clampMaxConcurrent(newValue as number) });
                          }}
                          min={1}
                          max={maxAllowedConcurrent}
                          step={1}
                          marks={[
                            { value: 1, label: '1' },
                            { value: maxAllowedConcurrent, label: String(maxAllowedConcurrent) }
                          ]}
                          valueLabelDisplay="auto"
                          size="small"
                        />
                      ) : maxAllowedConcurrent === null ? (
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={configForm.maxConcurrentCalls}
                          onChange={(event) => {
                            const parsed = parseInt(event.target.value || '1', 10);
                            setConfigForm({
                              ...configForm,
                              maxConcurrentCalls: Math.max(1, Number.isFinite(parsed) ? parsed : 1),
                            });
                          }}
                          inputProps={{ min: 1 }}
                        />
                      ) : (
                        <Typography variant="body2" color="error" sx={{ py: 1 }}>
                          {t('pages:campaigns.create.settings.noConcurrentAllowed')}
                        </Typography>
                      )}
                      {hasFiniteMaxConcurrent && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          {t('pages:campaigns.create.settings.maxConcurrentCallsHelp')}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('pages:campaigns.detail.config.maxConcurrentCalls')}
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {campaign.maxConcurrentCalls}
                      </Typography>
                    </Paper>
                  ),
                },
                {
                  id: 'retries',
                  label: t('pages:campaigns.detail.config.tabs.retries'),
                  icon: <Repeat sx={{ fontSize: 18 }} />,
                  content: configEditing ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        label={t('pages:campaigns.detail.config.maxRetries')}
                        type="number"
                        value={configForm.maxRetries}
                        onChange={(e) => setConfigForm({ ...configForm, maxRetries: parseInt(e.target.value) || 0 })}
                        size="small"
                        inputProps={{ min: 0, max: 10 }}
                      />
                      <TextField
                        label={t('pages:campaigns.create.settings.retryDelayMin')}
                        type="number"
                        value={configForm.retryDelayMinSeconds}
                        onChange={(e) => setConfigForm({ ...configForm, retryDelayMinSeconds: parseInt(e.target.value) || 0 })}
                        size="small"
                        inputProps={{ min: 0 }}
                      />
                      <TextField
                        label={t('pages:campaigns.create.settings.retryDelayMax')}
                        type="number"
                        value={configForm.retryDelayMaxSeconds}
                        onChange={(e) => setConfigForm({ ...configForm, retryDelayMaxSeconds: parseInt(e.target.value) || 0 })}
                        size="small"
                        inputProps={{ min: 0 }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2 }}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('pages:campaigns.detail.config.maxRetries')}
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {campaign.maxRetries}
                        </Typography>
                      </Paper>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('pages:campaigns.detail.config.retryDelay')}
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {campaign.retryDelayMinSeconds === campaign.retryDelayMaxSeconds
                            ? `${campaign.retryDelayMinSeconds}s`
                            : `${campaign.retryDelayMinSeconds}s - ${campaign.retryDelayMaxSeconds}s`}
                        </Typography>
                      </Paper>
                    </Box>
                  ),
                },
                ...(isAiCampaign ? [
                {
                  id: 'agents',
                  label: t('pages:campaigns.detail.config.tabs.agents'),
                  icon: <PsychologyIcon sx={{ fontSize: 18 }} />,
                  content: (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {resourcesError && (
                        <Alert severity="error">{resourcesError}</Alert>
                      )}
                      {resourcesLoading && (
                        <LinearProgress />
                      )}
                      {configEditing ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <FormControl size="small" sx={{ minWidth: 260, maxWidth: 420 }}>
                            <InputLabel>{t('pages:campaigns.detail.config.selectAgent')}</InputLabel>
                            <Select
                              value={configSelectedAgentId}
                              label={t('pages:campaigns.detail.config.selectAgent')}
                              onChange={(event) => setConfigSelectedAgentId(event.target.value as string)}
                            >
                              {availableAgents.map((agent) => (
                                <MenuItem key={agent.id} value={agent.id}>
                                  {agent.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {campaign?.aiAgentName && (
                            <Typography variant="caption" color="text.secondary">
                              {t('pages:campaigns.detail.config.currentAgent')}: {campaign.aiAgentName}
                            </Typography>
                          )}
                          {availableAgents.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                              {t('pages:campaigns.detail.noAgents')}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {campaign?.aiAgentName ? (
                            <Chip
                              label={campaign.aiAgentName}
                              size="small"
                              variant="outlined"
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {t('pages:campaigns.detail.noAgents')}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  ),
                },
                ] : []),
                ...(hasScriptsTab ? [
                {
                  id: 'scripts',
                  label: t('pages:campaigns.detail.config.tabs.automation'),
                  icon: <Code sx={{ fontSize: 18 }} />,
                  content: configEditing ? (
                    availableScripts.length === 0 ? (
                      <Alert severity="info">
                        {t('pages:campaigns.create.settings.noScripts')}{' '}
                        <Button size="small" onClick={() => navigate('/scripts/new')}>
                          {t('pages:campaigns.create.settings.createScript')}
                        </Button>
                      </Alert>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {t('pages:campaigns.create.settings.scriptsHelp')}
                        </Typography>
                        {availableScripts.map((script) => (
                          <Paper
                            key={script.id}
                            sx={{
                              p: 2,
                              cursor: 'pointer',
                              border: '2px solid',
                              borderColor: configSelectedScriptIds.includes(script.id) ? 'primary.main' : 'transparent',
                              bgcolor: configSelectedScriptIds.includes(script.id) ? alpha('#1976d2', 0.05) : 'background.paper',
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                            onClick={() => toggleConfigScriptSelection(script.id)}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                              <Checkbox
                                checked={configSelectedScriptIds.includes(script.id)}
                                onChange={() => toggleConfigScriptSelection(script.id)}
                              />
                              <Box>
                                <Typography fontWeight={600}>{script.name}</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                  <Chip
                                    label={eventLabels[script.eventType] || script.eventType}
                                    size="small"
                                    variant="outlined"
                                  />
                                  {script.description && (
                                    <Typography variant="caption" color="text.secondary">
                                      {script.description.substring(0, 50)}{script.description.length > 50 ? '...' : ''}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    )
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {preCallStatus?.enabled && preCallStatus.policy && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={t(`pages:scripts.preCall.modes.${preCallStatus.policy.mode}`)}
                            size="small"
                            color={preCallStatus.policy.mode === 'gate' ? 'primary' : 'default'}
                          />
                          <Chip
                            label={t(`pages:campaigns.detail.preCall.breaker.${preCallStatus.breaker.state}`)}
                            size="small"
                            color={preCallStatus.breaker.state === 'open' ? 'warning' : 'default'}
                            variant="outlined"
                          />
                          {Object.entries(preCallStatus.stateCounts).map(([state, count]) => (
                            <Chip
                              key={state}
                              label={`${t(`pages:campaigns.detail.preCall.states.${state}`)}: ${formatNumber(count)}`}
                              size="small"
                              variant="outlined"
                              onClick={() => togglePreCallState(state)}
                            />
                          ))}
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {scripts.length > 0 ? (
                          scripts.map((script) => (
                            <Chip
                              key={script.id}
                              label={`${script.name} - ${eventLabels[script.eventType] || script.eventType}`}
                              size="small"
                              variant="outlined"
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t('pages:campaigns.create.settings.noScripts')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ),
                },
                ] : []),
                ...(hasTransferTab ? [
                {
                  id: 'transfers',
                  label: t('pages:campaigns.detail.config.tabs.transfers'),
                  icon: <PhoneInTalk sx={{ fontSize: 18 }} />,
                  content: (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('pages:campaigns.create.transfers.description')}
                      </Typography>
                      {configEditing ? (
                        <>
                          <FormControl fullWidth size="small">
                            <InputLabel>{t('pages:campaigns.create.transfers.targetType')}</InputLabel>
                            <Select
                              value={configTransferTargetType}
                              label={t('pages:campaigns.create.transfers.targetType')}
                              onChange={(event) => {
                                const nextType = event.target.value as CampaignTransferTargetType | '';
                                setConfigTransferTargetType(nextType);
                                setConfigTransferTargetId('');
                              }}
                            >
                              <MenuItem value="user">{t('pages:campaigns.create.transfers.targetTypeUser')}</MenuItem>
                              <MenuItem value="group" disabled={transferGroups.length === 0}>
                                {t('pages:campaigns.create.transfers.targetTypeGroup')}
                              </MenuItem>
                            </Select>
                          </FormControl>

                          {configTransferTargetType === 'user' && (
                            <FormControl fullWidth size="small">
                              <InputLabel>{t('pages:campaigns.create.transfers.targetUser')}</InputLabel>
                              <Select
                                value={configTransferTargetId}
                                label={t('pages:campaigns.create.transfers.targetUser')}
                                onChange={(event) => setConfigTransferTargetId(event.target.value as string)}
                              >
                                {transferUsers.map((entry) => (
                                  <MenuItem key={entry.id} value={entry.id}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                      <Typography variant="body2">{entry.name}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {t('pages:campaigns.create.transfers.userExtension', { extension: entry.extension })}
                                      </Typography>
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}

                          {configTransferTargetType === 'group' && (
                            transferGroups.length === 0 ? (
                              <Alert severity="warning">
                                {t('pages:campaigns.create.transfers.noGroups')}
                              </Alert>
                            ) : (
                              <FormControl fullWidth size="small">
                                <InputLabel>{t('pages:campaigns.create.transfers.targetGroup')}</InputLabel>
                                <Select
                                  value={configTransferTargetId}
                                  label={t('pages:campaigns.create.transfers.targetGroup')}
                                  onChange={(event) => setConfigTransferTargetId(event.target.value as string)}
                                >
                                  {transferGroups.map((entry) => (
                                    <MenuItem key={entry.id} value={entry.id}>
                                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="body2">{entry.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {t('pages:campaigns.create.transfers.groupMembers', { count: entry.membersCount })}
                                          {' • '}
                                          {t(`pages:campaigns.create.transfers.groupStrategy.${entry.strategy}`)}
                                        </Typography>
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )
                          )}

                          {configTransferTargetType === '' && (
                            <Alert severity="info">
                              {t('pages:campaigns.create.transfers.selectionRequired')}
                            </Alert>
                          )}

                          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={configSuspendWhenNoTransferOperatorAvailable}
                                  onChange={(event) => setConfigSuspendWhenNoTransferOperatorAvailable(event.target.checked)}
                                />
                              }
                              label={t('pages:campaigns.create.transfers.suspendWhenNoOperator')}
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {t('pages:campaigns.create.transfers.suspendWhenNoOperatorHelp')}
                            </Typography>
                          </Paper>
                        </>
                      ) : (
                        <Box>
                          {campaign?.transferTargetType && campaign.transferTargetId ? (
                            <Paper sx={{ p: 2 }}>
                              <Typography variant="caption" color="text.secondary">
                                {t('pages:campaigns.create.transfers.targetType')}
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {campaign.transferTargetType === 'user'
                                  ? t('pages:campaigns.create.transfers.targetTypeUser')
                                  : t('pages:campaigns.create.transfers.targetTypeGroup')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                {campaign.transferTargetType === 'user'
                                  ? t('pages:campaigns.create.transfers.targetUser')
                                  : t('pages:campaigns.create.transfers.targetGroup')}
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {campaign.transferTargetType === 'user'
                                  ? (transferUsers.find((entry) => entry.id === String(campaign.transferTargetId))?.name || '—')
                                  : (transferGroups.find((entry) => entry.id === String(campaign.transferTargetId))?.name || '—')}
                              </Typography>
                            </Paper>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {t('pages:campaigns.create.transfers.selectionRequired')}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  ),
                },
                ] : []),
                {
                  id: 'schedule',
                  label: t('pages:campaigns.detail.config.tabs.schedule'),
                  icon: <AccessTime sx={{ fontSize: 18 }} />,
                  content: configEditing ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('pages:campaigns.create.settings.scheduleHelp')}
                      </Typography>
                      {configForm.schedule.map((entry, index) => {
                        const entryErrors = getEntryErrorsEdit(index);
                        const hasErrors = entryErrors.length > 0;
                        const { minTime, maxTime } = getTimeConstraintsEdit(entry);
                        return (
                          <Paper
                            key={index}
                            sx={{
                              p: 2,
                              bgcolor: 'background.default',
                              border: hasErrors ? '2px solid' : 'none',
                              borderColor: hasErrors ? 'error.main' : 'transparent',
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccessTime fontSize="small" /> {t('pages:campaigns.create.settings.scheduleEntry', { number: index + 1 })}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setConfigForm(prev => ({
                                    ...prev,
                                    schedule: prev.schedule.filter((_, i) => i !== index)
                                  }));
                                }}
                                color="error"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                {t('pages:campaigns.create.settings.scheduleDays')}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map(day => {
                                  const isSelected = entry.days.includes(day);
                                  const globalDay = operatingHours[day];
                                  const isGlobalEnabled = globalDay?.enabled ?? false;
                                  return (
                                    <Chip
                                      key={day}
                                      label={t(`common:days.${day}`)}
                                      size="small"
                                      disabled={!isGlobalEnabled}
                                      color={isSelected ? 'primary' : 'default'}
                                      variant={isSelected ? 'filled' : 'outlined'}
                                      onClick={() => {
                                        if (!isGlobalEnabled) return;
                                        setConfigForm(prev => ({
                                          ...prev,
                                          schedule: prev.schedule.map((e, i) => {
                                            if (i !== index) return e;
                                            const newDays = e.days.includes(day)
                                              ? e.days.filter(d => d !== day)
                                              : [...e.days, day];
                                            const keepAllDay = Boolean(
                                              e.allDay
                                                && newDays.length > 0
                                                && newDays.every(selectedDay => operatingHours[selectedDay]?.allDay)
                                            );
                                            const fallbackDay = newDays[0] || day;
                                            return {
                                              ...e,
                                              days: newDays,
                                              allDay: keepAllDay,
                                              start: keepAllDay ? null : (e.start || operatingHours[fallbackDay]?.start || '09:00'),
                                              end: keepAllDay ? null : (e.end || operatingHours[fallbackDay]?.end || '18:00'),
                                            };
                                          })
                                        }));
                                      }}
                                      sx={{
                                        cursor: isGlobalEnabled ? 'pointer' : 'not-allowed',
                                        opacity: isGlobalEnabled ? 1 : 0.5,
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            </Box>
                            {(() => {
                              const canUseAllDay = entry.days.length > 0
                                && entry.days.every(day => Boolean(operatingHours[day]?.enabled && operatingHours[day]?.allDay));
                              return (
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={Boolean(entry.allDay)}
                                      disabled={!canUseAllDay}
                                      onChange={(event) => {
                                        const checked = event.target.checked;
                                        setConfigForm(prev => ({
                                          ...prev,
                                          schedule: prev.schedule.map((ent, i) => {
                                            if (i !== index) return ent;
                                            const fallbackDay = ent.days[0] || 'mon';
                                            return {
                                              ...ent,
                                              allDay: checked,
                                              start: checked ? null : (ent.start || operatingHours[fallbackDay]?.start || '09:00'),
                                              end: checked ? null : (ent.end || operatingHours[fallbackDay]?.end || '18:00'),
                                            };
                                          }),
                                        }));
                                      }}
                                    />
                                  }
                                  label={t('pages:campaigns.create.settings.scheduleAllDay')}
                                  sx={{ mb: 1 }}
                                />
                              );
                            })()}
                            {!entry.allDay ? (
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                  <TimePicker
                                    label={t('pages:campaigns.create.settings.scheduleStart')}
                                    value={entry.start ? dayjs(`2000-01-01T${entry.start}`) : null}
                                    onChange={(value: Dayjs | null) => {
                                      const timeStr = value ? value.format('HH:mm') : '';
                                      setConfigForm(prev => ({
                                        ...prev,
                                        schedule: prev.schedule.map((ent, i) =>
                                          i === index ? { ...ent, start: timeStr } : ent
                                        )
                                      }));
                                    }}
                                    ampm={false}
                                    minTime={minTime ? dayjs(`2000-01-01T${minTime}`) : undefined}
                                    maxTime={maxTime ? dayjs(`2000-01-01T${maxTime}`) : undefined}
                                    slotProps={{
                                      textField: {
                                        size: 'small',
                                        sx: { flex: 1 },
                                        error: entryErrors.some(e => e.type === 'time_invalid' || e.type === 'outside_limits'),
                                      }
                                    }}
                                  />
                                  <Typography variant="body2" color="text.secondary">—</Typography>
                                  <TimePicker
                                    label={t('pages:campaigns.create.settings.scheduleEnd')}
                                    value={entry.end ? dayjs(`2000-01-01T${entry.end}`) : null}
                                    onChange={(value: Dayjs | null) => {
                                      const timeStr = value ? value.format('HH:mm') : '';
                                      setConfigForm(prev => ({
                                        ...prev,
                                        schedule: prev.schedule.map((ent, i) =>
                                          i === index ? { ...ent, end: timeStr } : ent
                                        )
                                      }));
                                    }}
                                    ampm={false}
                                    minTime={minTime ? dayjs(`2000-01-01T${minTime}`) : undefined}
                                    maxTime={maxTime ? dayjs(`2000-01-01T${maxTime}`) : undefined}
                                    slotProps={{
                                      textField: {
                                        size: 'small',
                                        sx: { flex: 1 },
                                        error: entryErrors.some(e => e.type === 'time_invalid' || e.type === 'outside_limits'),
                                      }
                                    }}
                                  />
                                </Box>
                              </LocalizationProvider>
                            ) : null}
                            {entry.days.length > 0 && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                {t('pages:campaigns.create.settings.scheduleGlobalLimit')}: {' '}
                                {entry.days.map(day => {
                                  const globalDay = operatingHours[day];
                                  if (!globalDay?.enabled) return null;
                                  if (globalDay.allDay) {
                                    return `${t(`common:days.${day}`)} ${t('pages:campaigns.create.settings.scheduleAllDayShort')}`;
                                  }
                                  return `${t(`common:days.${day}`)} ${globalDay.start}-${globalDay.end}`;
                                }).filter(Boolean).join(', ')}
                              </Typography>
                            )}
                            {entryErrors.length > 0 && (
                              <Box sx={{ mt: 1.5 }}>
                                {entryErrors.map((error, errorIdx) => (
                                  <Alert key={errorIdx} severity="error" sx={{ mb: 0.5, py: 0 }}>
                                    {error.message}
                                  </Alert>
                                ))}
                              </Box>
                            )}
                          </Paper>
                        );
                      })}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          variant="outlined"
                          startIcon={<Add />}
                          onClick={() => {
                            const enabledDays = Object.entries(operatingHours)
                              .filter(([_, config]) => config.enabled)
                              .map(([day]) => day);
                            const defaultDays = ['mon', 'tue', 'wed', 'thu', 'fri'].filter(d => enabledDays.includes(d));
                            const defaultStart = operatingHours[defaultDays[0] || 'mon']?.start || '09:00';
                            const defaultEnd = operatingHours[defaultDays[0] || 'mon']?.end || '18:00';
                            setConfigForm(prev => ({
                              ...prev,
                              schedule: [...prev.schedule, {
                                days: defaultDays.length > 0 ? defaultDays : [],
                                allDay: false,
                                start: defaultStart,
                                end: defaultEnd,
                              }]
                            }));
                          }}
                          size="small"
                        >
                          {t('pages:campaigns.create.settings.addScheduleEntry')}
                        </Button>
                        <Button
                          variant="text"
                          startIcon={<RestartAlt />}
                          onClick={resetScheduleToDefaults}
                          color="secondary"
                          size="small"
                        >
                          {t('pages:campaigns.create.settings.scheduleResetToDefault')}
                        </Button>
                      </Box>
                      {scheduleErrors.length > 0 && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          {t('pages:campaigns.create.settings.scheduleValidationError')}
                        </Alert>
                      )}
                    </Box>
                  ) : (
                    <Box>
                      {configForm.schedule.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {configForm.schedule.map((entry, index) => (
                            <Chip
                              key={index}
                              icon={<AccessTime sx={{ fontSize: 16 }} />}
                              label={`${entry.days.map(d => t(`common:days.${d}`)).join(', ')} ${entry.start}-${entry.end}`}
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('pages:campaigns.create.settings.noScheduleInfo')}
                        </Typography>
                      )}
                    </Box>
                  ),
                },
              ]}
            />
          )}
        </DialogContent>
        <DialogActions>
          {configEditing ? (
            <>
              <Button onClick={() => setConfigEditing(false)} disabled={configSaving}>
                {t('pages:campaigns.detail.config.cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveConfig}
                disabled={configSaving || scheduleErrors.length > 0}
              >
                {configSaving ? t('pages:campaigns.detail.config.saving') : t('pages:campaigns.detail.config.save')}
              </Button>
            </>
          ) : (
            <Button onClick={handleCloseConfigModal}>{t('pages:campaigns.detail.config.close')}</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      {ConfirmDialogComponent}
      <CampaignConcurrencyConflictDialog
        open={Boolean(concurrencyConflict)}
        conflict={concurrencyConflict}
        onClose={() => setConcurrencyConflict(null)}
      />

      <CampaignReportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        campaignId={id}
        stats={stats}
        onGeneratingChange={setIsExporting}
      />

      {/* Clone Dialog */}
      <Dialog
        open={cloneDialogOpen}
        onClose={() => {
          setCloneDialogOpen(false);
          resetCloneDraft();
        }}
        maxWidth="md"
        fullWidth
        fullScreen={isSmallScreen}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FileCopy sx={{ color: 'primary.main' }} />
            {t('pages:campaigns.detail.clone.title')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('pages:campaigns.detail.clone.description')}
          </Typography>

          {cloneResourcesError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {cloneResourcesError}
            </Alert>
          )}

          <TextField
            fullWidth
            label={t('pages:campaigns.detail.clone.newName')}
            placeholder={t('pages:campaigns.detail.clone.newNamePlaceholder')}
            value={cloneName}
            onChange={(e) => setCloneName(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle2" gutterBottom>
            {t('pages:campaigns.detail.clone.contactSourceTitle')}
          </Typography>
          <RadioGroup
            value={cloneContactSource}
            onChange={(event) => {
              setCloneContactSource(event.target.value as CloneContactSource);
              setCloneResourcesError(null);
            }}
            sx={{ mb: 3, ml: 1 }}
          >
            <FormControlLabel
              value="campaign_contacts"
              control={<Radio />}
              label={t('pages:campaigns.detail.clone.contactSourceCampaign')}
            />
            <FormControlLabel
              value="uploaded_file"
              control={<Radio />}
              label={t('pages:campaigns.detail.clone.contactSourceUploadedFile')}
            />
          </RadioGroup>

          {cloneContactSource === 'campaign_contacts' ? (
            <>
              <Typography variant="subtitle2" gutterBottom>
                {t('pages:campaigns.detail.clone.excludeTitle')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={cloneFilters.excludeConverted}
                      onChange={(event) => setCloneFilters((prev) => ({ ...prev, excludeConverted: event.target.checked }))}
                    />
                  }
                  label={t('pages:campaigns.detail.clone.excludeConvertedDetail')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={cloneFilters.excludeOptout}
                      onChange={(event) => setCloneFilters((prev) => ({ ...prev, excludeOptout: event.target.checked }))}
                    />
                  }
                  label={t('pages:campaigns.detail.clone.excludeOptoutDetail')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={cloneFilters.excludeReached}
                      onChange={(event) => setCloneFilters((prev) => ({ ...prev, excludeReached: event.target.checked }))}
                    />
                  }
                  label={t('pages:campaigns.detail.clone.excludeReachedDetail')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={cloneFilters.excludeNoValidPhones}
                      onChange={(event) => setCloneFilters((prev) => ({ ...prev, excludeNoValidPhones: event.target.checked }))}
                    />
                  }
                  label={t('pages:campaigns.detail.clone.excludeNoValidPhonesDetail')}
                />
                {isCloneReverseIvrCampaign && (
                  <>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={cloneFilters.excludeHasDtmf}
                          onChange={(event) => setCloneFilters((prev) => ({ ...prev, excludeHasDtmf: event.target.checked }))}
                        />
                      }
                      label={t('pages:campaigns.detail.clone.excludeHasDtmfDetail')}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={cloneFilters.excludeHeardAudioToEnd}
                          onChange={(event) => setCloneFilters((prev) => ({ ...prev, excludeHeardAudioToEnd: event.target.checked }))}
                        />
                      }
                      label={t('pages:campaigns.detail.clone.excludeHeardAudioToEndDetail')}
                    />
                  </>
                )}
              </Box>

              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                {t('pages:campaigns.detail.clone.phoneFiltersTitle')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={cloneFilters.excludeInvalidPhones}
                      onChange={(event) => setCloneFilters((prev) => ({ ...prev, excludeInvalidPhones: event.target.checked }))}
                    />
                  }
                  label={t('pages:campaigns.detail.clone.excludeInvalidPhonesDetail')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={cloneFilters.excludeMismatchPhones}
                      onChange={(event) => setCloneFilters((prev) => ({ ...prev, excludeMismatchPhones: event.target.checked }))}
                    />
                  }
                  label={t('pages:campaigns.detail.clone.excludeMismatchPhonesDetail')}
                />
              </Box>

              {stats && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {t('pages:campaigns.detail.clone.summary', {
                    reached: formatNumber(stats.reachedContacts),
                    optout: formatNumber(stats.optoutContacts),
                    exhausted: formatNumber(stats.exhaustedContacts),
                  })}
                </Alert>
              )}
            </>
          ) : (
            <Paper sx={{ p: 2.5, mb: 2 }} variant="outlined">
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                {t('pages:campaigns.detail.clone.contactsFileTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('pages:campaigns.detail.clone.contactsFileHelp')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button variant="outlined" component="label">
                  {cloneContactsFile
                    ? t('pages:campaigns.detail.clone.changeContactsFile')
                    : t('pages:campaigns.detail.clone.selectContactsFile')}
                  <input
                    hidden
                    type="file"
                    accept=".csv,.xlsx,.xls,.txt"
                    onChange={handleCloneContactsFileSelect}
                  />
                </Button>
                <Typography variant="body2" color={cloneContactsFile ? 'text.primary' : 'text.secondary'}>
                  {cloneContactsFile?.name || t('pages:campaigns.detail.clone.noContactsFileSelected')}
                </Typography>
              </Box>

              <Divider sx={{ my: 2.5 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                <Typography variant="subtitle2">
                  {t('pages:campaigns.detail.clone.historicalFiltersTitle')}
                </Typography>
                <Tooltip title={t('pages:campaigns.detail.clone.historicalFiltersHelp')} arrow>
                  <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={cloneHistoricalImportFilters.removePreviouslyConvertedContacts}
                      onChange={(event) => setCloneHistoricalImportFilters((prev) => ({
                        ...prev,
                        removePreviouslyConvertedContacts: event.target.checked,
                      }))}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {t('pages:campaigns.detail.clone.removePreviouslyConvertedContacts')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('pages:campaigns.detail.clone.removePreviouslyConvertedContactsHelp')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={cloneHistoricalImportFilters.removePreviouslyReachedContacts}
                      onChange={(event) => setCloneHistoricalImportFilters((prev) => ({
                        ...prev,
                        removePreviouslyReachedContacts: event.target.checked,
                      }))}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {t('pages:campaigns.detail.clone.removePreviouslyReachedContacts')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('pages:campaigns.detail.clone.removePreviouslyReachedContactsHelp')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={cloneHistoricalImportFilters.removePreviouslyOptoutContacts}
                      onChange={(event) => setCloneHistoricalImportFilters((prev) => ({
                        ...prev,
                        removePreviouslyOptoutContacts: event.target.checked,
                      }))}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {t('pages:campaigns.detail.clone.removePreviouslyOptoutContacts')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('pages:campaigns.detail.clone.removePreviouslyOptoutContactsHelp')}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Paper>
          )}

          {isCloneReverseIvrCampaign && (
            <Paper sx={{ p: 2.5, mt: 3 }} variant="outlined">
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                {t('pages:campaigns.detail.clone.ivrAudioTitle')}
              </Typography>

              {cloneResourcesLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    {t('pages:campaigns.detail.clone.loadingResources')}
                  </Typography>
                </Box>
              )}

              <RadioGroup
                value={cloneIvrAudioSource}
                onChange={(event) => {
                  setCloneIvrAudioSource(event.target.value as CloneIvrAudioSource);
                  setCloneIvrAudioError(null);
                }}
                sx={{ ml: 1 }}
              >
                <FormControlLabel
                  value="keep"
                  control={<Radio />}
                  label={t('pages:campaigns.detail.clone.ivrAudioSourceKeep')}
                />
                <FormControlLabel
                  value="existing"
                  control={<Radio />}
                  label={t('pages:campaigns.detail.clone.ivrAudioSourceExisting')}
                />
                <FormControlLabel
                  value="uploaded"
                  control={<Radio />}
                  label={t('pages:campaigns.detail.clone.ivrAudioSourceUploaded')}
                />
              </RadioGroup>

              {cloneIvrAudioSource === 'keep' && (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 1 }}>
                  {t('pages:campaigns.detail.clone.ivrAudioKeepCurrent', {
                    name: selectedCloneReverseIvrAudioOption?.name || campaign?.ivrAudioFileId || '—',
                  })}
                </Typography>
              )}

              {cloneIvrAudioSource === 'existing' && (
                <Autocomplete
                  sx={{ mt: 2 }}
                  options={cloneReverseIvrAudioOptions}
                  value={selectedCloneReverseIvrAudioOption}
                  onChange={(_, nextOption) => {
                    setCloneSelectedReverseIvrAudioId(nextOption?.id || '');
                    setCloneIvrAudioError(null);
                  }}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  noOptionsText={t('pages:campaigns.create.basicInfo.ivrAudioNoExisting', 'Nenhum áudio disponível')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('pages:campaigns.detail.clone.ivrAudioExistingLabel')}
                      placeholder={t('pages:campaigns.detail.clone.ivrAudioExistingPlaceholder')}
                    />
                  )}
                />
              )}

              {cloneIvrAudioSource === 'uploaded' && (
                <Box sx={{ mt: 2 }}>
                  <WaveformPlayerField
                    url={cloneIvrAudioPreviewUrl}
                    displayName={cloneIvrAudioFile?.name}
                    downloadFilename={cloneIvrAudioFile?.name}
                    durationSeconds={cloneIvrAudioDurationSeconds}
                    uploadEnabled
                    required
                    onSelectFile={handleCloneIvrAudioSelect}
                    disabled={cloneSubmitting}
                    constraints={{
                      maxDurationSeconds: cloneIvrMaxAudioSeconds,
                    }}
                    showConstraints
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {t('pages:campaigns.create.basicInfo.ivrAudioHelp', { maxSeconds: cloneIvrMaxAudioSeconds })}
                  </Typography>
                </Box>
              )}

              {cloneIvrAudioError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {cloneIvrAudioError}
                </Alert>
              )}
            </Paper>
          )}

          {cloneSubmitting && cloneSubmitPhase && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {cloneSubmitPhase === 'uploading_contacts'
                  ? t('pages:campaigns.detail.clone.uploadingContacts')
                  : cloneSubmitPhase === 'uploading_audio'
                    ? t('pages:campaigns.detail.clone.uploadingAudio')
                    : t('pages:campaigns.detail.clone.cloning')}
              </Typography>
              <LinearProgress
                variant={cloneUploadProgress !== null ? 'determinate' : 'indeterminate'}
                value={cloneUploadProgress ?? undefined}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCloneDialogOpen(false);
              resetCloneDraft();
            }}
            disabled={cloneSubmitting}
          >
            {t('pages:campaigns.detail.clone.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleClone}
            startIcon={cloneSubmitting ? <CircularProgress size={16} color="inherit" /> : <FileCopy />}
            disabled={cloneSubmitDisabled}
          >
            {cloneSubmitting
              ? t('pages:campaigns.detail.clone.cloning')
              : t('pages:campaigns.detail.clone.clone')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
